import { z } from "zod";

/**
 * Shared DTO cho AI assistant — chat + conversations + context + NL query (US-E11.F1-0064-S02 /
 * @UC0071 · US-E11.F1-0065 context · US-E11.F2-0066 query). Một nguồn sự thật api ↔ mobile (S04).
 *
 * BẤT BIẾN (invariants):
 *  - Money BigInt→string (KHÔNG float): mọi field số dư/tiền là string minor VND. Response .strict()
 *    chặn field lạ/secret lọt ra (userId nội bộ KHÔNG serialize).
 *  - Read-only ở subtask này: proposal record là NHÁP (resolved=false) — execute ⇐ S03.
 *  - IDOR: server lọc theo token (ownerUserId) — contract KHÔNG mang field cross-user.
 *  - Cursor opaque string đối xứng common/pagination/cursor (envelope {data, pageInfo}).
 */

/** Độ dài tối đa 1 câu chat (chống prompt phình to / DoS token). */
export const AI_MESSAGE_MAX_LENGTH = 2000;
/** Độ dài tối đa cursor opaque tại biên. */
export const AI_CURSOR_MAX_LENGTH = 512;
/** Max page-size list hội thoại. */
export const AI_CONVERSATIONS_MAX_LIMIT = 50;

/** Banner khi AI tắt cho nhóm (AC10) — một nguồn sự thật api ↔ mobile. */
export const AI_DISABLED_MESSAGE = "Trợ lý AI đang tắt cho nhóm này";

/**
 * Banner khi AI chạy MÔ PHỎNG (mock — chưa cấu hình LLM thật). KHÔNG đánh lừa: nói
 * rõ câu trả lời là demo tất-định, không phải AI thật. Một nguồn sự thật api ↔ mobile.
 */
export const AI_SIMULATED_MESSAGE =
  "Trợ lý đang chạy ở chế độ demo (chưa cấu hình AI thật) — câu trả lời chỉ mang tính minh hoạ.";

/** Intent phân loại turn AI (khớp domain ChatIntent + LlmIntent). */
export const aiChatIntentSchema = z.enum(["answer", "clarify", "propose"]);
export type AiChatIntent = z.infer<typeof aiChatIntentSchema>;

/** role tin nhắn: me (người dùng) | ai (trợ lý). */
export const aiMessageRoleSchema = z.enum(["me", "ai"]);
export type AiMessageRole = z.infer<typeof aiMessageRoleSchema>;

// ─── US-AI.F4-0233 (@UC9400) — SCHEMA DÙNG CHUNG: proactive + lỗi + preview ────────────────────────

/**
 * 🔴 FIDELITY (@UC9400 · AC5) — MỘT flow output, HAI người đọc.
 *
 * `aiProactiveFlowSchema` là hình dạng DUY NHẤT của một flow do OrchestrationService sinh ra. App THẬT
 * (GET /v1/ai/context → `proactive[]`) và ADMIN PREVIEW (POST /v1/admin/ai/preview → `proactive[]`)
 * cùng parse bằng schema NÀY. Lệch schema = preview nói dối ⇒ contract test bắt ngay.
 *
 * Money: KHÔNG có field tiền thô ở đây — `messages` đã format "500.000 đ" ở StepRunner (@AI-NUM-01).
 */
/**
 * 🔴 US-AI-0295-S02 — deep-link CÓ NHÃN đọc được, không chỉ chuỗi route.
 *
 * 🔴 VÁ MIGRATION LÀM DỞ (phát hiện 22/07): S02 đã đổi `FlowOutput.deepLinks` từ `string[]` sang
 * `DeepLink[]` ở domain VÀ ở Flutter (`AiDeepLink.listFrom`), nhưng **quên contract + admin**. Hậu quả
 * tiềm ẩn: `aiContextResponseSchema.parse()` nhận object vào `z.string()` ⇒ **500 ở GET /v1/ai/context**
 * ngay khi có flow kèm deep-link; và admin render `{d}` ⇒ `[object Object]`.
 * Test không bắt được vì user trong int-spec chỉ khớp flow LỜI CHÀO (deep-link rỗng ⇒ mảng rỗng lọt
 * qua `z.array(z.string())`). Đúng họ "xanh vì chưa chạm tới", không phải "xanh vì đúng".
 */
export const aiDeepLinkSchema = z
  .object({
    /** Khoá route trong ROUTE_CATALOG — client map sang route THẬT của mình. */
    key: z.string(),
    /** Đường dẫn đã điền tham số. Thiếu tham số ⇒ engine BỎ nút, không phát nửa vời. */
    path: z.string(),
    labelVi: z.string(),
    labelEn: z.string(),
  })
  .strict();
export type AiDeepLink = z.infer<typeof aiDeepLinkSchema>;

export const aiProactiveFlowSchema = z
  .object({
    /** Mã scenario trace về docs/ai-scenarios/registry.json. */
    code: z.string(),
    /** Câu chữ đã điền biến (theo locale). */
    messages: z.array(z.string()),
    /** Route đích + NHÃN — client điều hướng, KHÔNG tự bịa route, cũng không tự bịa nhãn nút. */
    deepLinks: z.array(aiDeepLinkSchema),
    /** @SEC-ONB-01 — flow này đòi dialog cảnh báo NẶNG trước khi cho ghi tiền. */
    requiresHeavyConfirm: z.boolean(),
  })
  .strict();
export type AiProactiveFlow = z.infer<typeof aiProactiveFlowSchema>;

/**
 * 🔴 US-AI-0295-S03 (@UC9400) — MỘT lượt chào gộp, thay cho N bong bóng rời.
 *
 * PO 21/07: mở trợ lý ra hiện 3 bong bóng kèm mã kỹ thuật `SEC-ONB-01 · xác nhận nặng` — vô nghĩa với
 * phụ huynh. `proactive[]` GIỮ NGUYÊN (client cũ + trace không vỡ); đây là lớp TRÌNH BÀY đi kèm, do
 * `OrchestrationService` dựng ⇒ app thật và admin preview nhận CÙNG một thứ.
 *
 * `code` có mặt để trace/telemetry nhưng UI **KHÔNG ĐƯỢC** ghép nó vào câu chữ hiển thị.
 */
/**
 * 🔴 US-AI-0439-S05 (@UC9688) — CTA của thẻ là **discriminated union theo `kind`**, đúng khuôn
 * `redacted` mà repo đã dùng: trạng thái sai KHÔNG BIỂU DIỄN ĐƯỢC. Một object phẳng mang cả `deepLink`
 * lẫn `prompt` sẽ tạo ra "thẻ vừa là chip vừa là deep-link", và client phải tự đoán bấm cái nào.
 *
 * `chip` chạy TRONG chat nên luôn dùng được; `deep_link` phụ thuộc màn đã dựng ở client.
 */
export const aiGreetingCtaSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("chip"), label: z.string().min(1), prompt: z.string().min(1) }).strict(),
  z
    .object({ kind: z.literal("deep_link"), label: z.string().min(1), deepLink: aiDeepLinkSchema })
    .strict(),
]);
export type AiGreetingCta = z.infer<typeof aiGreetingCtaSchema>;

/** Lý do thẻ KHÔNG có nút — khai tường minh (PK-2). Không nút + không lý do = người dùng tưởng app hỏng. */
export const aiGreetingCtalessSchema = z.enum(["waiting_on_others", "informational", "blocked"]);

export const aiGreetingItemSchema = z
  .object({
    code: z.string(),
    /** id flow DUY NHẤT — `code` là mã usecase DÙNG CHUNG (5 flow cùng `AI-ONB-03`). */
    flowId: z.string(),
    /** Tiêu đề thẻ. `text` giữ nguyên giá trị này cho client cũ. */
    title: z.string(),
    /** @deprecated = `title`. Giữ để admin preview + client cũ không vỡ. */
    text: z.string(),
    /** 1-2 câu VÌ SAO nên làm — đến từ catalog, tầng trình bày không bịa. */
    detail: z.string().optional(),
    /** Bước kế tiếp (chỉ bậc `SETUP`). */
    next: z.string().optional(),
    groupId: z.string().optional(),
    /** BẮT BUỘC (ở tầng dựng) khi user đa nhóm — thẻ phải nói việc này của nhóm nào. */
    groupName: z.string().optional(),
    cta: aiGreetingCtaSchema.optional(),
    ctaless: aiGreetingCtalessSchema.optional(),
    /** Vắng = flow không có deep-link ⇒ vẫn hiện dòng, chỉ thiếu nút (không loại bỏ việc). */
    deepLink: aiDeepLinkSchema.optional(),
  })
  .strict();
export type AiGreetingItem = z.infer<typeof aiGreetingItemSchema>;

/** Bậc việc — closed-set, thứ tự mảng LÀ thứ hạng (nguồn: `FLOW_BANDS` của domain). */
export const aiFlowBandSchema = z.enum([
  "BLOCKED",
  "IN_FLIGHT",
  "DEADLINE",
  "DUTY",
  "SETUP",
  "HYGIENE",
  "IDLE",
]);
export type AiFlowBand = z.infer<typeof aiFlowBandSchema>;

/**
 * 🔴 US-AI-0441-S03 (@UC9698) — chip GỢI Ý NĂNG LỰC cho lượt không có việc nào.
 *
 * Hình dạng trùng nhánh `chip` của `aiGreetingCtaSchema` (nhãn = câu sẽ gửi) — bấm là gửi đúng câu đó
 * qua router hiện có, không đẻ đường vào thứ hai.
 */
/**
 * 🔴 US-AI-0442-S01 (@UC9699) — "bỏ qua" một NUDGE VỆ SINH.
 *
 * Hai chế độ, phân loại theo HẬU QUẢ (QĐ người dùng 17/08) — và **nhãn nút phải nói đúng điều sẽ xảy
 * ra**: `forever` ⇒ nút ghi *"Bỏ qua"*; `snooze` ⇒ nút ghi *"Để sau"*. Ghi *"Bỏ qua"* rồi 30 ngày sau
 * hiện lại là nói dối người dùng, và trong app tiền mất niềm tin ở chuyện nhỏ là mất ở chuyện lớn.
 */
export const aiNudgeDismissModeSchema = z.enum(["forever", "snooze"]);
export type AiNudgeDismissMode = z.infer<typeof aiNudgeDismissModeSchema>;

export const aiNudgeDismissRequestSchema = z
  .object({ mode: aiNudgeDismissModeSchema })
  .strict();
export type AiNudgeDismissRequest = z.infer<typeof aiNudgeDismissRequestSchema>;

export const aiNudgeDismissResponseSchema = z
  .object({
    nudgeId: z.string().min(1),
    mode: aiNudgeDismissModeSchema,
    /** Thời điểm nudge sẽ hiện lại; `null` = không bao giờ (`forever`). Client hiển thị được sự thật. */
    suppressedUntil: z.string().datetime().nullable(),
  })
  .strict();
export type AiNudgeDismissResponse = z.infer<typeof aiNudgeDismissResponseSchema>;

export const aiGreetingSuggestedChipSchema = z
  .object({ label: z.string().min(1), prompt: z.string().min(1) })
  .strict();
export type AiGreetingSuggestedChip = z.infer<typeof aiGreetingSuggestedChipSchema>;

export const aiGreetingTurnSchema = z
  .object({
    greeting: z.string(),
    items: z.array(aiGreetingItemSchema),
    /**
     * 🔴 US-AI-0441-S03 (@UC9698) — gợi ý năng lực THẬT (theo vai + config), CHỈ khi `isEmptyState`.
     * Mặc định `[]` ⇒ client cũ không vỡ. Nội dung do `capabilityChips` dựng — **không hard-code**,
     * nếu không sẽ gợi ý một việc vừa bị tắt cho nhóm, tức nói dối về năng lực.
     */
    suggestedChips: z.array(aiGreetingSuggestedChipSchema).default([]),
    requiresHeavyConfirm: z.boolean(),
    /** Không việc nào ⇒ UI hiện chip gợi ý thay vì khoảng trống. */
    isEmptyState: z.boolean(),
    /**
     * 🔴 US-AI-0439-S05 (@UC9688) — BẬC đang được hiện. `null` khi không có việc nào.
     * Phơi ra để admin preview + telemetry thấy engine **chọn bậc nào**, thay vì phải suy từ danh sách
     * item (suy ngược = một bản chép thứ hai của luật chọn bậc).
     */
    band: aiFlowBandSchema.nullable(),
  })
  .strict();
export type AiGreetingTurn = z.infer<typeof aiGreetingTurnSchema>;


/**
 * 🔴 US-AI-0298-S03 (@UC9400) — MỘT BƯỚC engine ĐÃ THỰC SỰ chạy. Phản chiếu `TraceStep` ở
 * `@goppy/domain` (`orchestration/trace.ts`) — **một nguồn sự thật về hình dạng**, contract chỉ là bản
 * sao trên dây.
 *
 * `at` là SỐ THỨ TỰ (không timestamp) ⇒ tất định, và **tăng liên tục XUYÊN CÁC LƯỢT** của một phiên:
 * đó là thứ biến trace từ "ảnh chụp một lượt" thành "dòng thời gian phiên" — chính chỗ mà bug
 * duplicate-turn / Huỷ-không-huỷ từng lọt vì không ai nhìn thấy.
 */
export const traceStepSchema = z
  .object({
    stage: z.enum([
      "rule_eval",
      "flow_run",
      "intent_classify",
      "form_open",
      "prefill",
      "hydrate_options",
      "validate",
      "step_advance",
      "confirm",
      "execute",
    ]),
    /** Phân biệt USER làm gì vs AI làm gì — yêu cầu PO, không trộn thành một dòng chung. */
    actor: z.enum(["user", "ai"]),
    decision: z.string(),
    codes: z.array(z.string()),
    at: z.number().int().nonnegative(),
    details: z.record(z.unknown()).optional(),
  })
  .strict();
export type TraceStepDto = z.infer<typeof traceStepSchema>;

/**
 * Lớp GIẢI THÍCH (admin preview + debug app): vì sao ra kết quả đó. .strict().
 *
 * 🔴 US-AI-0298-S03: THÊM `trace` — **MỞ RỘNG, không thay thế**. `flowsMatched`/`rulesFired`/
 * `priorities` giữ nguyên vì contract test đang khoá chúng.
 * `.default([])` ⇒ client cũ / response thiếu field vẫn parse được thành mảng rỗng, **không crash**.
 * Rỗng nghĩa là "engine không phát bước nào" — TUYỆT ĐỐI không bịa bước cho đủ pipeline.
 */
export const aiOrchestrationExplainSchema = z
  .object({
    flowsMatched: z.array(z.string()),
    rulesFired: z.array(z.string()),
    priorities: z.array(z.number().int()),
    locale: z.enum(["vi", "en"]),
    trace: z.array(traceStepSchema).default([]),
  })
  .strict();
export type AiOrchestrationExplain = z.infer<typeof aiOrchestrationExplainSchema>;

/**
 * 🔴 @AI-ERR-01 @AI-FAI-01 @AI-FAI-02 — LỖI CÓ MÃ ỔN ĐỊNH (hết "một toast cho mọi lỗi").
 * Client (T6) render theo `code`; `message` chỉ là câu chữ mặc định theo locale.
 */
export const aiErrorCodeSchema = z.enum([
  "RATE_LIMIT",
  "BUDGET_EXCEEDED",
  "LLM_TIMEOUT",
  "NETWORK",
  "UNAUTHORIZED",
  "AI_DISABLED",
  "DEMO_MODE",
  "CANCELLED",
  "SERVER_ERROR",
]);
export type AiErrorCodeDto = z.infer<typeof aiErrorCodeSchema>;

export const aiErrorPayloadSchema = z
  .object({
    code: aiErrorCodeSchema,
    message: z.string(),
    /** Thử lại có ích không (BUDGET_EXCEEDED = false ⇒ client KHÔNG hiện nút Thử lại). */
    retryable: z.boolean(),
    /** @AI-ERR-03 @AI-FAIL-19 — lỗi phạm vi CẢ NHÓM (UI nói "cả nhóm", không nói "bạn"). */
    groupScoped: z.boolean(),
    /** @AI-FAI-02 — lượt này THẤT BẠI (client tô đỏ + nút Thử lại), không giả vờ thành công. */
    status: z.literal("failed"),
    retryAfterMs: z.number().int().nonnegative().optional(),
  })
  .strict();
export type AiErrorPayload = z.infer<typeof aiErrorPayloadSchema>;

// ─── POST /v1/ai/chat ──────────────────────────────────────────────────────

/**
 * Body chat. scope = 'all' (cross-group từ Home) HOẶC fundId (UUID — chat trong 1 quỹ). conversationId
 * optional (vắng → tạo hội thoại mới). .strict() chặn field lạ (over-post).
 */
export const aiChatRequestSchema = z
  .object({
    message: z.string().trim().min(1).max(AI_MESSAGE_MAX_LENGTH),
    scope: z.union([z.literal("all"), z.string().uuid()]),
    conversationId: z.string().uuid().optional(),
  })
  .strict();
export type AiChatRequest = z.infer<typeof aiChatRequestSchema>;

/** Loại đề xuất write. record_paid=ghi nhận đóng (income) · expense=khoản chi · reminder=nhắc · mutate=sửa/xoá. */
export const aiProposalKindSchema = z.enum([
  "record_paid",
  "expense",
  "reminder",
  "mutate",
]);
export type AiProposalKind = z.infer<typeof aiProposalKindSchema>;

/**
 * Payload proposal NHÁP (chỉ intent=propose). resolved=false LUÔN ở subtask này (S03 confirm/execute).
 * amountMinor string minor VND (no-float); memberName/period nullable (mutate proposal không có).
 *
 * `id` (S03): UUID bản ghi proposal đã persist → mobile card gọi confirm/cancel qua `/ai/proposals/:id`.
 * Vắng khi proposal chưa persist (không xảy ra ở luồng chuẩn — server luôn set). resolved LUÔN false ở
 * response chat (proposal nháp — chưa execute; execute qua confirm endpoint).
 */
export const aiProposalSchema = z
  .object({
    id: z.string().uuid(),
    kind: aiProposalKindSchema,
    memberName: z.string().nullable(),
    amountMinor: z.string().nullable(),
    resolved: z.literal(false),
    /** Câu chat gốc (server-authoritative) — S03 dùng để execute; KHÔNG do LLM chốt số. */
    rawMessage: z.string(),
    /**
     * 🔴 US-AI.F2-0231 B3 (@UC9400 @SEC-TRUST-02) — THẺ ĐỀ XUẤT PHẢI NÓI RÕ TIỀN VÀO **QUỸ NÀO / NHÓM
     * NÀO**. Bug thật: thẻ chỉ hiện "Ghi nhận Minh 500k → Xác nhận"; người có nhiều quỹ (quỹ lớp + quỹ
     * hội đồng hương + quỹ nhà) không cách nào biết mình đang ghi vào quỹ nào ⇒ ghi nhầm quỹ ⇒ sổ cái
     * append-only, phải đảo bút toán để sửa. Server (không phải LLM) điền tên THẬT từ DB.
     * Default null ⇒ client cũ không vỡ; server luôn set ở luồng chat scope-fund.
     */
    fundName: z.string().nullable().default(null),
    groupName: z.string().nullable().default(null),
    /**
     * @SEC-LONG-01 — HẠN của thẻ (ISO). Client hiện đếm ngược + vô hiệu hoá nút Xác nhận khi hết hạn;
     * server VẪN chốt (confirm sau hạn → 409, 0 bút toán). null = thẻ cũ (trước B3).
     */
    expiresAt: z.string().nullable().default(null),
  })
  .strict();
export type AiProposal = z.infer<typeof aiProposalSchema>;

// ─── POST /v1/ai/proposals/:id/confirm | :id/cancel (S03 · @UC0072 · @gate 🔴 money,ai) ────────────

/**
 * Kết quả xử lý proposal (confirm/cancel). resolved=true LUÔN (proposal đã đóng — execute hoặc huỷ).
 * outcome quyết định luồng đã đi:
 *  - executed_income → post CONTRIBUTION source=ai (ledgerTxId + amountMinor). Số dư tăng.
 *  - expense_posted  → khoản chi duyệt admin_only → post EXPENSE source=ai (ledgerTxId + expenseId).
 *  - vote_opened     → khoản chi mode vote → mở Consensus session (consensusId, CHƯA post ledger).
 *  - cooling_scheduled → 🔴 US-E19.F2-0201 (@UC9299): khoản chi ≥ ngưỡng cooling ⇒ HOÃN ghi sổ
 *    (status `cooling` + postAfter). **CHƯA post ledger** ⇒ `ledgerTxId` LUÔN null (AI KHÔNG được
 *    nói "đã duyệt ✓" khi tiền chưa rời quỹ — chống nói dối tiền).
 *  - reminder_sent   → nhắc đã gửi (KHÔNG ledger).
 *  - cancelled       → huỷ, 0 bút toán.
 * Money string minor VND (no-float). .strict() chặn field lạ.
 */
export const aiProposalOutcomeSchema = z.enum([
  "executed_income",
  "expense_posted",
  "vote_opened",
  "cooling_scheduled",
  "reminder_sent",
  "cancelled",
  /**
   * 🔴 US-AI.F4-0233 (@UC9400 @SEC-RISK-01 @SEC-RISK-02 @AI-RSK-18) — thẻ QUÁ HẠN bị SWEEPER đóng
   * (KHÔNG phải user huỷ ⇒ phải phân biệt được với `cancelled` khi truy vết). 0 bút toán.
   */
  "expired",
]);
export type AiProposalOutcome = z.infer<typeof aiProposalOutcomeSchema>;

export const aiProposalResultSchema = z
  .object({
    proposalId: z.string().uuid(),
    resolved: z.literal(true),
    outcome: aiProposalOutcomeSchema,
    /** Trả lời AI sau khi xử lý (toast/label). */
    reply: z.string(),
    /** Bút toán ledger tạo ra (income/expense_posted); null cho vote_opened/reminder/cancel. */
    ledgerTxId: z.string().uuid().nullable(),
    /** Khoản chi tạo ra (expense_posted/vote_opened); null nếu không áp. */
    expenseId: z.string().uuid().nullable(),
    /** Phiên biểu quyết mở ra (vote_opened); null nếu không áp. */
    consensusId: z.string().uuid().nullable(),
    /** Số tiền minor VND string (income/expense); null cho reminder/cancel. */
    amountMinor: z.string().nullable(),
  })
  .strict();
export type AiProposalResult = z.infer<typeof aiProposalResultSchema>;

/** Body confirm — Idempotency-Key qua header; body rỗng (proposal đã mang mọi fact server-authoritative). */
export const aiProposalConfirmRequestSchema = z.object({}).strict();
export type AiProposalConfirmRequest = z.infer<typeof aiProposalConfirmRequestSchema>;

/**
 * 🔴 US-AI.F2-0231 (@UC9400 @AI-RISK-03 @AI-RSK-02) — RE-PROPOSE **CÓ CẤU TRÚC**.
 *
 * BUG cũ: nút "Sửa" trên thẻ đề xuất ghép chuỗi tự nhiên ("Sửa đề xuất ghi nhận Minh 500k") rồi GỬI
 * LẠI như câu chat ⇒ câu ghép khớp động từ MUTATE ⇒ proposal mutate ⇒ confirm LUÔN từ chối ⇒ **VÒNG
 * LẶP CHẾT** (user sửa mãi không bao giờ hội tụ).
 *
 * NAY: client gửi PATCH FIELD trực tiếp (KHÔNG ghép câu, KHÔNG parse NLP lại). Server dựng proposal
 * MỚI từ proposal gốc + patch, giữ nguyên fund/actor (server-authoritative). Ít nhất 1 field phải có.
 * Tiền = STRING minor VND (no-float). .strict() chặn over-post.
 */
export const aiProposalRepproposeRequestSchema = z
  .object({
    /** Tên thành viên MỚI (vẫn resolve → memberId THẬT khi confirm — @SEC-WRONG-01). */
    memberName: z.string().trim().min(1).max(120).optional(),
    /** Số tiền MỚI: string minor VND nguyên dương (no-float). */
    amountMinor: z
      .string()
      .regex(/^[1-9]\d{0,18}$/, "amountMinor phải là số nguyên dương (minor VND)")
      .optional(),
  })
  .strict()
  .refine(
    (v) => v.memberName !== undefined || v.amountMinor !== undefined,
    "Cần ít nhất một thay đổi (tên thành viên hoặc số tiền).",
  );
export type AiProposalRepproposeRequest = z.infer<typeof aiProposalRepproposeRequestSchema>;

/**
 * Kết quả re-propose: proposal MỚI (nháp, resolved=false) thay cho proposal gốc (đã huỷ). Client hiển
 * thị lại thẻ xác nhận với số/tên ĐÃ SỬA — KHÔNG gửi câu chat nào (không vòng lặp chết).
 */
export const aiProposalRepproposeResponseSchema = z
  .object({
    /** proposal gốc (đã đóng — resolved=true, outcome=cancelled, 0 bút toán). */
    replacedProposalId: z.string().uuid(),
    proposal: aiProposalSchema,
    /**
     * 🔴 US-MOB.F6-0007 (@UC0072) — LƯỢT đã dựng sẵn cho thẻ mới.
     *
     * Vì sao thêm field này thay vì để client tự dựng thẻ từ `proposal`: client dựng nghĩa là client
     * tự **định dạng số tiền**, và hai bên định dạng khác nhau — server dùng `formatVndMinor`
     * (`"200.000đ"`), formatter dùng chung của mobile cho `"200.000 VND"`. Cùng một số tiền hiện hai
     * kiểu **trên cùng một màn hình**, tuỳ người dùng vừa bấm "Sửa" hay chưa.
     *
     * Định dạng khác nhau chưa làm sai sổ, nhưng nó làm người dùng nghi ngờ con số — mà niềm tin vào
     * con số chính là thứ sản phẩm này bán. Server dựng bảng ⇒ MỘT formatter, mọi bề mặt.
     *
     * `.optional()` ⇒ client cũ (`apps/flutter-web`) không vỡ.
     */
    turn: z.lazy(() => aiTurnSchema).optional(),
  })
  .strict();
export type AiProposalRepproposeResponse = z.infer<typeof aiProposalRepproposeResponseSchema>;

/**
 * Loại của MỘT lượt `role='ai'` trong lịch sử hội thoại. Whitelist ĐÓNG — enum, không `z.string()`.
 *
 * 🔴 US-E24.F13-0362 (@UC9610) — `'blocked'` là giá trị THẬT đang được ghi vào `ai_messages.kind` từ
 * `US-AI.F4-0233` (`ai-chat.service.ts` nhánh cổng chi phí / rate-limit / kill-switch). Enum ở đây
 * từng thiếu nó ⇒ `GET /v1/ai/conversations/:id` của BẤT KỲ hội thoại nào từng bị chặn ném ZodError
 * ⇒ **500**; và vì `ai_messages` APPEND-ONLY nên row đó không sửa được ⇒ **500 vĩnh viễn**.
 * Nới enum này KHÔNG đổi hành vi chặn — nó chỉ làm thứ đã bị chặn ĐỌC LẠI được.
 *
 * ⚠️ Thêm giá trị thứ 5 ở đường GHI mà quên chỗ này ⇒ tái diễn đúng bug trên. Guard tự-khám-phá
 * `ai-message-kind-contract.spec.ts` (apps/api) đối chiếu hai đầu và ĐỎ nếu lệch.
 */
export const aiMessageKindSchema = z.enum(["answer", "clarify", "proposal", "blocked"]);
export type AiMessageKind = z.infer<typeof aiMessageKindSchema>;

/**
 * Trạng thái HIỂN THỊ của một lượt trong lịch sử — nguồn DUY NHẤT để client phân biệt
 * "đã trả lời" với "bị chặn, KHÔNG làm gì".
 *
 * 🔴 US-E24.F13-0362 (@UC9610) — vì sao cần: `ai-conversation.service.ts:107-110` ghi thẳng lý do
 * `'blocked'` ra đời — *"Thủ quỹ đọc lại thread, thấy 15 lượt 'answer', tin là cả 15 người đã ghi —
 * trong khi 3 lượt cuối bị chặn và KHÔNG ghi gì."* Nới enum mà client vẫn render `blocked` y hệt
 * `answer` là vá NỬA VỜI: hết 500 nhưng quay lại đúng lời nói dối ban đầu. Trong hội thoại có tiền,
 * *"đã trả lời"* và *"bị chặn"* là hai sự thật khác nhau.
 *
 * Trả về `"failed"` — TÁI DÙNG đúng literal của `aiErrorPayloadSchema.status` (@AI-FAI-02: client tô
 * đỏ, không giả vờ thành công). Cố ý KHÔNG đẻ chuỗi tiếng Việt mới ở đây: câu chữ đến từ `error.code`
 * (CLAUDE.md §7 — backend trả `code`, không trả câu đã dịch).
 */
export function aiMessageTurnStatus(kind: AiMessageKind | null): "failed" | "ok" {
  return kind === "blocked" ? "failed" : "ok";
}

/**
 * Một tin nhắn trong response. proposal chỉ có khi kind=proposal; `kind='blocked'` = lượt BỊ CHẶN
 * (không có câu trả lời, không ghi gì — xem `aiMessageTurnStatus`). `kind=null` cho `role='me'`.
 * .strict() chặn field lạ.
 */
export const aiMessageSchema = z
  .object({
    id: z.string().uuid(),
    role: aiMessageRoleSchema,
    content: z.string(),
    kind: aiMessageKindSchema.nullable(),
    proposal: aiProposalSchema.nullable(),
    createdAt: z.string(),
  })
  .strict();
export type AiMessage = z.infer<typeof aiMessageSchema>;

/**
 * Response chat: conversationId (mới/cũ), intent, tin nhắn ai vừa tạo + proposal nháp (nếu propose).
 * aiEnabled=false → chỉ trả banner (message ai = AI_DISABLED_MESSAGE), KHÔNG orchestrate.
 */
export const aiChatResponseSchema = z
  .object({
    conversationId: z.string().uuid(),
    aiEnabled: z.boolean(),
    /**
     * true = câu trả lời do LLM MÔ PHỎNG (MockLlmClient — chưa cấu hình LLM thật
     * qua AI_ENABLED+ANTHROPIC_API_KEY). App hiện banner "chế độ demo" để KHÔNG
     * đánh lừa người dùng tưởng AI thật. aiEnabled=true nhưng simulated=true là
     * trạng thái RIÊNG (khác aiEnabled=false = tắt hẳn). Default false.
     */
    simulated: z.boolean().default(false),
    intent: aiChatIntentSchema,
    reply: z.string(),
    proposal: aiProposalSchema.nullable(),
    /**
     * 🔴 US-AI.F4-0233 (@UC9400 @AI-ERR-01 @AI-FAI-01 @AI-FAI-02) — LỖI CÓ MÃ ỔN ĐỊNH.
     * null = lượt chạy trơn. Có giá trị ⇒ lượt THẤT BẠI: client render theo `code` (không phải theo câu
     * chữ), tô đỏ tin nhắn (`status='failed'`), và CHỈ hiện nút "Thử lại" khi `retryable=true`.
     * Default null ⇒ client cũ không vỡ.
     */
    error: aiErrorPayloadSchema.nullable().default(null),
    /** @AI-ERR-04 @SEC-FAIL-02 — banner demo (chỉ khi `simulated`); null khi LLM thật. */
    demoBanner: z.string().nullable().default(null),
  })
  .strict();
export type AiChatResponse = z.infer<typeof aiChatResponseSchema>;

// ─── Conversations ─────────────────────────────────────────────────────────

/** GET /v1/ai/conversations — query cursor + limit. */
export const aiConversationsListQuerySchema = z.object({
  cursor: z.string().max(AI_CURSOR_MAX_LENGTH).optional(),
  limit: z.coerce.number().int().min(1).max(AI_CONVERSATIONS_MAX_LIMIT).optional(),
});
export type AiConversationsListQuery = z.infer<typeof aiConversationsListQuerySchema>;

/** Item hội thoại trong list (KHÔNG ownerUserId — chống leak). .strict(). */
export const aiConversationListItemSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    lastMessageAt: z.string(),
  })
  .strict();
export type AiConversationListItem = z.infer<typeof aiConversationListItemSchema>;

export const aiConversationsListResponseSchema = z
  .object({
    data: z.array(aiConversationListItemSchema),
    pageInfo: z.object({
      hasNext: z.boolean(),
      nextCursor: z.string().nullable(),
    }),
  })
  .strict();
export type AiConversationsListResponse = z.infer<typeof aiConversationsListResponseSchema>;

/** GET /v1/ai/conversations/:id — full message theo thứ tự (cũ→mới). .strict(). */
export const aiConversationDetailResponseSchema = z
  .object({
    id: z.string().uuid(),
    title: z.string(),
    messages: z.array(aiMessageSchema),
  })
  .strict();
export type AiConversationDetailResponse = z.infer<typeof aiConversationDetailResponseSchema>;

/** POST /v1/ai/conversations — tạo hội thoại rỗng. Response = item. */
export const aiCreateConversationResponseSchema = aiConversationListItemSchema;
export type AiCreateConversationResponse = z.infer<typeof aiCreateConversationResponseSchema>;

// ─── GET /v1/ai/context ────────────────────────────────────────────────────

/** Chip quick-reply (từ tool-registry đã lọc role+config). .strict(). */
export const aiChipSchema = z
  .object({
    name: z.string(),
    label: z.string(),
    prompt: z.string(),
    tier: z.enum(["read", "write"]),
  })
  .strict();
export type AiChip = z.infer<typeof aiChipSchema>;

/** Quỹ active trong context (số dư string minor VND; redacted → balance null). .strict(). */
export const aiContextFundSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    currency: z.string(),
    /** Số dư O(1) string minor VND; null khi Member + private (redaction fail-closed). */
    balance: z.string().nullable(),
    redacted: z.boolean(),
  })
  .strict();
export type AiContextFund = z.infer<typeof aiContextFundSchema>;

/** Nhóm trong context (cross-group → nhiều group; group cụ thể → 1). .strict(). */
export const aiContextGroupSchema = z
  .object({
    id: z.string().uuid(),
    name: z.string(),
    role: z.string(),
    funds: z.array(aiContextFundSchema),
    memberCount: z.number().int(),
  })
  .strict();
export type AiContextGroup = z.infer<typeof aiContextGroupSchema>;

export const aiContextResponseSchema = z
  .object({
    aiEnabled: z.boolean(),
    /** true = LLM mô phỏng (mock) — app hiện banner demo. Xem aiChatResponseSchema. */
    simulated: z.boolean().default(false),
    /** Chip scope: "Đang trong: [nhóm]·[quỹ]" hoặc "Tất cả nhóm". */
    scopeLabel: z.string(),
    greeting: z.string(),
    groups: z.array(aiContextGroupSchema),
    chips: z.array(aiChipSchema),
    /**
     * 🔴 US-AI.F4-0233 (@UC9400 · AC5 FIDELITY) — flow CHỦ ĐỘNG do OrchestrationService sinh (mảng theo
     * priority giảm dần). CÙNG schema với `POST /v1/admin/ai/preview → proactive[]` (một engine, hai
     * người gọi ⇒ preview KHÔNG nói dối). Mặc định [] ⇒ client cũ không vỡ.
     */
    proactive: z.array(aiProactiveFlowSchema).default([]),
    /** 🔴 US-AI-0295-S03 — lượt chào GỘP. Mặc định null ⇒ client cũ không vỡ. */
    greetingTurn: aiGreetingTurnSchema.nullable().default(null),
    /** Lớp giải thích (flow/rule/priority đã bắn) — cùng schema preview. */
    explain: aiOrchestrationExplainSchema.nullable().default(null),
    /** @AI-ERR-04 — banner demo (chỉ khi `simulated`); null khi LLM thật. */
    demoBanner: z.string().nullable().default(null),
    /**
     * 🔴🔴 US-AI-0443-S03 (@UC9704) — KHÔNG ĐỌC ĐƯỢC TÌNH HÌNH QUỸ lúc này.
     *
     * Vì sao trả **200 + cờ** thay vì 500: 500 làm màn chat trắng ⇒ người dùng nghĩ app hỏng hẳn.
     * Nhưng cờ là BẮT BUỘC — trả 200 mà giả vờ bình thường thì người dùng đọc sự im lặng thành
     * *"không có việc gì"*, tức **nói dối về tiền bằng sự im lặng**.
     *
     * Mặc định `false` ⇒ client cũ không vỡ.
     */
    degraded: z.boolean().default(false),
  })
  .strict();
export type AiContextResponse = z.infer<typeof aiContextResponseSchema>;

/** Query GET /v1/ai/context — groupId/fundId optional (vắng cả → cross-group). */
export const aiContextQuerySchema = z.object({
  groupId: z.string().uuid().optional(),
  fundId: z.string().uuid().optional(),
  /**
   * 🔴 US-AI-0438-S04 (@UC9685 · @A11Y-CROSS-01) — NGÔN NGỮ của lượt chào.
   *
   * `AiContextService` vốn NHẬN tham số `locale` nhưng controller không truyền gì ⇒ nó là **code chết**
   * và app thật luôn nhận tiếng Việt, trong khi admin preview (có `req.locale`) đổi được ⇒ **preview
   * không trung thực ở trục ngôn ngữ**. Client khai tường minh, `undefined` ⇒ service mặc định `"vi"`
   * (0 regress cho client cũ).
   */
  locale: z.enum(["vi", "en"]).optional(),
});
export type AiContextQuery = z.infer<typeof aiContextQuerySchema>;

// ─── POST /v1/ai/query (NL report, read-only) ──────────────────────────────

export const aiQueryRequestSchema = z
  .object({
    message: z.string().trim().min(1).max(AI_MESSAGE_MAX_LENGTH),
    scope: z.union([z.literal("all"), z.string().uuid()]),
  })
  .strict();
export type AiQueryRequest = z.infer<typeof aiQueryRequestSchema>;

/**
 * Loại kết quả query. clarify → hỏi lại (THIẾU KỲ THẬT); redirect_proposal → chuyển S03 (write-intent);
 * unsupported (@UC9400 @AI-AMB-02) → NĂNG LỰC CHƯA CÓ (vd hỏi chi tiêu/xuất file qua chat) ⇒ nói THẬT
 * + deepLink tới màn làm được việc đó, KHÔNG bịa "thiếu kỳ" rồi hỏi lại vô tận.
 */
export const aiQueryOutcomeSchema = z.enum([
  "balance",
  "unpaid",
  "income_total",
  /**
   * 🔴 Bug PO 20/07 — TỔNG CHI kỳ: Σ DEBIT tx txType∈{EXPENSE,LOAN_OUT} trong kỳ (CÙNG luật
   * fund-dashboard). Read-only; số ở field `expenseMinor`. Trước đây hỏi chi trả `unsupported`.
   */
  "expense_total",
  /** 🔴 US-AI-0271-S7 — danh sách thành viên ACTIVE (đọc qua GroupsService, CÙNG guard GUI). */
  "member_list",
  /**
   * 🔴 US-AI-0421 đợt 2 (@UC9433) — LINK MỜI đang hiệu lực (metadata: còn hạn / hết hạn khi nào).
   *
   * ⚠️ KHÔNG BAO GIỜ mang token thô: token chỉ trả MỘT LẦN lúc phát hành. Lượt chat được lưu ở
   * `ai_messages` — bảng **append-only**, nên một cái link lọt vào đó là lọt VĨNH VIỄN, không scrub
   * lại được. `inviteLinkListItemSchema` cố ý chỉ có id/hạn/người mời, và handler giữ đúng thế.
   */
  "invite_links",
  /**
   * 🔴 US-AI-0421 (@UC9433) — HỒ SƠ NHÓM: tên, mục đích, số thành viên, vai của chính người hỏi.
   * KHÁC `member_list` (danh sách tên từng người) — hai mô tả mờ nhau là hai tool cướp traffic nhau.
   */
  "group_profile",
  /**
   * 🔴 US-AI-0295-S10 — LIỆT KÊ từng khoản chi trong kỳ (khác `expense_total` = một con số tổng).
   * Đọc qua `TransactionsService.listTransactions(type=EXPENSE)` — CÙNG redaction như GUI,
   * AI KHÔNG đi thẳng DB (QĐ PO 21/07).
   */
  "expense_list",
  /**
   * 🔴 US-AI-0295-S10 — GIAO DỊCH GẦN NHẤT (cả thu lẫn chi), KHÔNG cần kỳ.
   * Đọc qua `TransactionsService.listTransactions` — cùng service + redaction như màn Lịch sử giao
   * dịch của GUI ⇒ chat là TẬP CON nghiêm ngặt của thứ user đã xem được, không phải cửa hậu.
   */
  "recent_tx",
  /**
   * 🔴🔴 US-AI-0425 (@UC9706) — DỮ LIỆU CỦA CHÍNH NGƯỜI DÙNG.
   *
   * Bốn outcome này tồn tại vì một sai sót phân loại: 5 endpoint từng bị xếp vào ô `gui-only`, tức
   * dùng ô đó làm CHỖ GIẤU VIỆC CHƯA LÀM. Luật của người dùng: *"phủ 100% = thao tác các bước SAU
   * KHI ĐÃ ĐĂNG NHẬP"*.
   *
   * `my_groups` chạy được cả khi người hỏi **chưa có quỹ nào** — đó là câu mở đầu tự nhiên nhất của
   * người mới.
   * ⚠️ `my_linked_accounts` CHỈ tên nhà cung cấp — token/mật khẩu KHÔNG BAO GIỜ rời API, và lượt chat
   * lưu ở `ai_messages` (append-only) nên lọt vào đó là lọt vĩnh viễn.
   */
  "my_profile",
  "my_groups",
  "my_linked_accounts",
  /**
   * 🔴🔴 US-AI-0508 (@UC0060 @UC0055 @UC9659 @UC9596) — HAI câu hỏi trỏ vào MỘT NGƯỜI.
   *
   * `member_contributions` đọc qua `MemberContributionsService` (CÙNG service màn "Chi tiết thành
   * viên" của mobile dùng) ⇒ hai trục quyền nằm SẴN ở đó: **404 đồng nhất** cho người không được mở
   * hồ sơ (không phải chủ suất, không phải O/A/T) và **`'•••'`, KHÔNG `0`** cho vai bị ẩn tiền
   * (§3.8 — `0` bịa ra lời cáo buộc *"người này chưa đóng đồng nào"*). Chat KHÔNG dựng luật thứ hai.
   *
   * `member_accounts` đọc qua `MemberAccountMappingService` — 0 tiền, và RBAC của endpoint gốc ĐÃ
   * NỚI 16/08 (`US-E24.F8-0420`): mọi thành viên ACTIVE đọc được, KHÔNG còn O/A-only. Dòng cũ ở
   * ngay trên file này từng chép luật cũ — đã sửa cùng task.
   */
  "member_contributions",
  "member_accounts",
  /**
   * 🔴🔴 US-AI-0509 (@UC0074 @UC9591 @UC9593) — DÒNG THỜI GIAN CỦA CẢ NHÓM.
   *
   * Đọc qua `ActivityFeedService.listActivity` — CÙNG hàm `GET /v1/groups/:id/activity` của GUI dùng
   * ⇒ **hai trục redaction (§7 P5.3) nằm SẴN ở đó và chat không dựng luật thứ hai**: số tiền theo
   * `transparency_mode`, tên người nộp theo `payment_status_visibility`. Hai trục ĐỘC LẬP — nhóm có
   * thể minh bạch tiền mà vẫn giấu tên, hoặc ngược lại.
   *
   * 🔴 KHÁC `recent_tx` (giao dịch trong sổ của MỘT QUỸ): feed kể cả việc **không dính tiền** — ai
   * vào nhóm, ai rời nhóm. Số tiền bị ẩn ⇒ trợ lý in `'•••'`, **KHÔNG `0`** (§3.8: `0` là một con
   * số, và nói "khoản này 0 đồng" khi thật ra "bạn không được xem" là nói dối về tiền).
   *
   * ⚠️ Như `member_contributions`, outcome này KHÔNG có mảng dữ liệu riêng cấp response: nội dung
   * nằm trong `reply` (mobile chỉ đọc chuỗi đó — bài học 0302 P1), mọi field tiền để `null`.
   */
  "group_activity",
  "clarify",
  "unsupported",
  /**
   * 🔴 US-AI.F2-0231 B3 (@UC9400 @AI-DIS-02 @SEC-DISC-01) — user hỏi "trợ lý làm được gì?" ⇒ trả
   * MANIFEST NĂNG LỰC THẬT (làm được / KHÔNG làm được / cảnh báo chat GHI ĐƯỢC vào sổ cái thật).
   */
  "capabilities",
  "redirect_proposal",
]);
export type AiQueryOutcome = z.infer<typeof aiQueryOutcomeSchema>;

/** Một dòng member chưa đóng (unpaid query). amountMinor string. .strict(). */
export const aiUnpaidMemberSchema = z
  .object({
    memberName: z.string().nullable(),
    /**
     * 🔴 US-AI-0302 (audit 22/07, 3/3 skeptic) — **null khi người hỏi KHÔNG có quyền xem tiền của
     * người khác**. GUI đã chốt luật này từ trước và chat phải theo:
     *  · `MembersPaymentStatusService:47` — *"Select CHỈ name + avatar + status (KHÔNG amountMinor
     *    người khác)"*, mở cho MỌI member active;
     *  · `PaymentBoardController:35` — bảng CÓ tiền, đòi `OWNER/ADMIN/TREASURER`.
     * Trước bản vá, chat trả TÊN + SỐ TIỀN còn nợ của mọi người cho member thường ⇒ chat thành
     * **cửa hậu** đọc đúng thứ GUI cố ý giấu.
     * `null` (KHÔNG phải `"0"`): 0 là **nói dối về tiền** — client hiện `—`/`•••`.
     */
    amountMinor: z.string().nullable(),
    status: z.string(),
  })
  .strict();
export type AiUnpaidMember = z.infer<typeof aiUnpaidMemberSchema>;

/** 🔴 US-AI-0271-S7 — một thành viên trong danh sách (member_list). KHÔNG lộ email/phone (PII). */
export const aiGroupMemberSchema = z
  .object({
    /** Tên hiển thị; null khi chưa đặt (KHÔNG bịa tên thay người dùng). */
    memberName: z.string().nullable(),
    role: z.string(),
  })
  .strict();
export type AiGroupMember = z.infer<typeof aiGroupMemberSchema>;

/**
 * 🔴 US-AI-0295-S10 — MỘT khoản chi trong danh sách. Tiền = **string minor** (no-float, B1).
 * `memo` có thể null (bút toán không ghi chú) ⇒ client hiện "(không có ghi chú)", KHÔNG bịa nội dung.
 */
export const aiExpenseItemSchema = z
  .object({
    id: z.string(),
    amountMinor: z.string(),
    memo: z.string().nullable(),
    /** ISO — client tự format theo locale người xem. */
    occurredAt: z.string(),
  })
  .strict();
export type AiExpenseItem = z.infer<typeof aiExpenseItemSchema>;

/**
 * Response query read-only. outcome quyết định field nào có mặt:
 *  - balance → balanceMinor + asOf (timestamp AC1).
 *  - income_total → incomeMinor + period.
 *  - expense_total → expenseMinor + period (bug PO 20/07).
 *  - expense_list → expenses[] + period (US-AI-0295-S10 — DANH SÁCH, khác con số tổng).
 *  - unpaid → unpaidMembers[].
 *  - member_list → members[] (US-AI-0271-S7).
 *  - clarify → reply (câu hỏi lại).
 *  - redirect_proposal → reply (chuyển proposal flow — AC6).
 * Money string minor VND (no-float). .strict() chặn field lạ.
 */
/**
 * 🔴 US-AI-0295-S10 — MỘT giao dịch trong danh sách gần nhất.
 * Khác `aiExpenseItemSchema`: có `direction` vì recent_tx TRỘN thu và chi — thiếu nó thì client
 * không phân biệt được tiền vào hay tiền ra, tức là hiển thị sai HƯỚNG của tiền.
 */
export const aiRecentTxItemSchema = z
  .object({
    id: z.string(),
    direction: z.enum(["income", "expense"]),
    amountMinor: z.string(),
    memo: z.string().nullable(),
    occurredAt: z.string(),
  })
  .strict();
export type AiRecentTxItem = z.infer<typeof aiRecentTxItemSchema>;

export const aiQueryResponseSchema = z
  .object({
    outcome: aiQueryOutcomeSchema,
    reply: z.string(),
    /** Số dư (balance) — string minor VND; null nếu redacted/không áp. */
    balanceMinor: z.string().nullable(),
    /** Tổng thu (income_total) — string minor VND; null nếu không áp. */
    incomeMinor: z.string().nullable(),
    /**
     * 🔴 Bug PO 20/07 — Tổng chi (expense_total) — string minor VND; null nếu không áp.
     * `.default(null)` ⇒ client cũ (chưa biết field này) không vỡ.
     */
    expenseMinor: z.string().nullable().default(null),
    /** Kỳ 'YYYY-MM' (income_total/expense_total/unpaid); null nếu không áp. */
    period: z.string().nullable(),
    /** Timestamp "đọc lúc" (AC1) ISO; null nếu clarify/redirect. */
    asOf: z.string().nullable(),
    /** Member chưa đóng (unpaid); [] nếu không áp. */
    unpaidMembers: z.array(aiUnpaidMemberSchema),
    /**
     * 🔴 US-AI-0271-S7 — thành viên nhóm (member_list); [] nếu không áp.
     * TÁCH khỏi `unpaidMembers` có chủ ý: "thành viên của nhóm" ≠ "người đang nợ tiền" — gộp hai
     * khái niệm vào một mảng là mời gọi client hiển thị nhầm danh sách nợ thành danh sách thành viên.
     * `.default([])` ⇒ client cũ không vỡ.
     */
    members: z.array(aiGroupMemberSchema).default([]),
    /**
     * 🔴 US-AI-0295-S10 — từng khoản chi trong kỳ (expense_list); [] nếu không áp.
     * TÁCH khỏi `expenseMinor` (một con số) có chủ ý: user hỏi "chi những khoản nào" muốn DANH SÁCH.
     * Tiền vẫn là **string minor** (no-float) — client tự format, server không gửi số đã định dạng.
     */
    expenses: z.array(aiExpenseItemSchema).default([]),
    /** 🔴 US-AI-0295-S10 — giao dịch gần nhất (recent_tx); [] nếu không áp. */
    recentTx: z.array(aiRecentTxItemSchema).default([]),
    /**
     * @UC9400 @AI-AMB-02 — route GUI làm được việc AI chưa hỗ trợ (outcome=unsupported). null nếu không áp.
     * Mặc định null ⇒ client cũ không vỡ.
     */
    deepLink: z.string().nullable().default(null),
  })
  .strict();
export type AiQueryResponse = z.infer<typeof aiQueryResponseSchema>;

// ─── POST /v1/admin/ai/preview ─────────────────────────────────────────────

/** Chế độ preview: `dry_run` KHÔNG ghi sổ (mô tả bút toán); `live` ghi vào DB SANDBOX. */
export const aiPreviewModeSchema = z.enum(["dry_run", "live"]);
export type AiPreviewMode = z.infer<typeof aiPreviewModeSchema>;

/**
 * Bộ GIẢ LẬP LỖI — admin xem được ĐƯỜNG HỎNG (giá trị nhất của preview).
 * `demo_mode` = ép LLM mô phỏng (@SEC-FAIL-02 — phải CẤM ghi tiền).
 */
export const aiPreviewSimulateSchema = z.enum([
  "none",
  "rate_limit",
  "budget",
  "timeout",
  "confirm_fail",
  "duplicate_name",
  "demo_mode",
  "offline",
  // US-E23.F5-0335-S01 (@UC9521) — 4 ĐƯỜNG HỎNG trước đây KHÔNG thử được ở preview.
  //
  // 🔴 Vì sao đáng thêm: mỗi mã dưới đây đòi người dùng làm MỘT VIỆC KHÁC HẲN, và admin không có
  // cách nào xem câu chữ thật trước khi nó xảy ra với người dùng:
  //  · `unauthorized`  ⇒ đăng nhập lại (KHÔNG phải "thử lại sau" — thử lại là vô ích)
  //  · `ai_disabled`   ⇒ nhờ quản trị viên bật trợ lý (người dùng tự làm gì cũng không được)
  //  · `cancelled`     ⇒ chính người dùng bấm Huỷ (KHÔNG phải lỗi — không được hiện như lỗi)
  //  · `server_error`  ⇒ fallback DUY NHẤT, phải nói được là "lỗi của chúng tôi", không đổ cho user
  "unauthorized",
  "ai_disabled",
  "cancelled",
  "server_error",
]);
export type AiPreviewSimulate = z.infer<typeof aiPreviewSimulateSchema>;

export const aiPreviewRequestSchema = z
  .object({
    /** User TRONG SANDBOX để đóng vai (KHÔNG phải user thật — runner chốt lại). */
    actingAsUserId: z.string().uuid(),
    mode: aiPreviewModeSchema.default("dry_run"),
    simulate: aiPreviewSimulateSchema.default("none"),
    message: z.string().trim().min(1).max(AI_MESSAGE_MAX_LENGTH).optional(),
    locale: z.enum(["vi", "en"]).default("vi"),
    /** Quỹ đích khi thử đường GHI TIỀN (reactive). Vắng ⇒ chỉ chạy proactive. */
    fundId: z.string().uuid().optional(),
    /**
     * US-E23.F5-0335-S03 (@UC9523) — PHIÊN hội thoại preview. Vắng ⇒ `"preview"` (hành vi cũ,
     * 0 regress cho client đang chạy).
     *
     * 🔴 VÌ SAO CẦN: trước đây preview ghim ngữ cảnh bằng HẰNG CỨNG `"preview"` theo `actingAsUserId`.
     * Hai tab admin (hoặc hai kịch bản chạy xen kẽ) **giẫm lên nhau**: tab A đang hỏi dở "kỳ nào?",
     * tab B gửi câu khác ⇒ ngữ cảnh của A bị ghi đè ⇒ A trả lời cho câu hỏi của B. Admin đọc kết quả
     * đó rồi kết luận về hành vi AI — kết luận dựa trên một lượt lai giữa hai kịch bản.
     *
     * Client tự sinh (uuid mỗi tab/kịch bản); server KHÔNG suy từ đâu khác.
     */
    conversationId: z.string().uuid().optional(),
  })
  .strict();
export type AiPreviewRequest = z.infer<typeof aiPreviewRequestSchema>;

/**
 * Mô tả BÚT TOÁN SẼ GHI ở `dry_run` (KHÔNG ghi thật). Tiền = string minor VND (no-float).
 * `live` ⇒ `posted=true` + `ledgerTxId` THẬT trong sandbox.
 */
export const aiPreviewLedgerPreviewSchema = z
  .object({
    /** true = ĐÃ ghi (mode=live); false = chỉ mô tả (mode=dry_run). */
    posted: z.boolean(),
    txType: z.string(),
    /** Dòng bút toán: side + accountKind + amount minor (string). */
    lines: z.array(
      z
        .object({
          side: z.enum(["DEBIT", "CREDIT"]),
          accountKind: z.string(),
          amountMinor: z.string(),
          memberId: z.string().nullable(),
        })
        .strict(),
    ),
    /** Câu mô tả cho admin đọc ("bút toán SẼ là: DEBIT CASH ... / CREDIT MEMBER_RECEIVABLE ..."). */
    description: z.string(),
    ledgerTxId: z.string().nullable(),
  })
  .strict();
export type AiPreviewLedgerPreview = z.infer<typeof aiPreviewLedgerPreviewSchema>;

/**
 * 🔴 @AI-TRU-01 (G10-trust) — BẰNG CHỨNG của đề xuất tiền.
 *
 * Bug gốc: thẻ đề xuất chỉ có 3 dòng chữ ("Ghi thu / Minh / 500.000đ") ⇒ thủ quỹ xác nhận bằng NIỀM
 * TIN thuần tuý vào chữ AI vừa sinh. KHÔNG thấy: Minh NÀO (nhóm có hai "Minh"), Minh đang nợ bao
 * nhiêu, quỹ nào, số dư quỹ TRƯỚC và SAU khi ghi, số này LẤY TỪ ĐÂU.
 *
 * Khối này là NGUỒN của con số: định danh THẬT (memberId, không phải tên), danh sách ứng viên đã cân
 * nhắc (chứng minh chọn đúng người), số nợ hiện tại, số dư quỹ trước/sau, và `sourceOfNumber` nói rõ
 * số tiền do ENGINE bóc từ câu người dùng (DP-3: LLM KHÔNG sinh số).
 *
 * Money = string minor (BigInt serialize, no-float). `null` ở field nào = KHÔNG có bằng chứng field đó
 * (UI phải nói "chưa có bằng chứng", TUYỆT ĐỐI không hiển thị 0 giả).
 */
export const aiPreviewEvidenceSchema = z
  .object({
    /** Định danh THẬT của người nhận bút toán (không phải tên hiển thị). */
    memberId: z.string(),
    memberDisplayName: z.string().nullable(),
    /** Số ứng viên trùng tên engine đã cân nhắc (>1 ⇒ đã hỏi lại, không đoán). */
    candidatesConsidered: z.number().int().min(0),
    fundId: z.string(),
    fundName: z.string().nullable(),
    /** Còn nợ hiện tại của THÀNH VIÊN ĐÓ (minor string) — null nếu không đọc được. */
    memberOutstandingMinor: z.string().nullable(),
    /** Số dư quỹ TRƯỚC / SAU bút toán (minor string) — null nếu không đọc được. */
    fundBalanceBeforeMinor: z.string().nullable(),
    fundBalanceAfterMinor: z.string().nullable(),
    /** Số tiền đề xuất (minor string) — đối chiếu với dòng bút toán. */
    amountMinor: z.string(),
    /** NGUỒN con số: engine bóc từ câu người dùng (KHÔNG do LLM bịa). */
    sourceOfNumber: z.string(),
  })
  .strict();
export type AiPreviewEvidence = z.infer<typeof aiPreviewEvidenceSchema>;

/** Kind CTA — quyết cách render + hành vi client (@UC9410 §1). */
export const ctaKindSchema = z.enum([
  "chip",
  "deep_link",
  "submit",
  "confirm",
  "reject",
  "edit",
  "retry",
  "choose",
]);
export type CtaKind = z.infer<typeof ctaKindSchema>;

/**
 * 🔴 CTA — call-to-action cụ thể. Bất biến "không ngõ cụt": mọi AiTurn ≥1 CtaAction user bấm được.
 *  - kind=chip → `prompt` (câu gửi lại khi bấm).
 *  - kind=deep_link → `deepLink` (route GUI DP-7).
 *  - kind=choose → `value` (value gửi lại).
 */
export const ctaActionSchema = z
  .object({
    kind: ctaKindSchema,
    label: z.string().trim().min(1),
    prompt: z.string().optional(),
    deepLink: z.string().optional(),
    value: z.string().optional(),
    /**
     * US-E23.F5-0335-S04 (@UC9522) — CTA `kind=choose` đang chọn CÁI GÌ (`fundId` | `memberId` | …).
     *
     * 🔴 SERVER-AUTHORITATIVE, và đây là điểm chính. Trước đây `choiceKey` chỉ tồn tại ở type LOCAL
     * của admin (`AiTurnRenderer.tsx`) và được bơm client-side, nên client phải ĐOÁN loại choose từ
     * ngữ cảnh. Nó đoán bằng luật *"vắng choiceKey ⇒ chắc là chọn quỹ"* — đúng chừng nào `choose`
     * chỉ có một nguồn duy nhất là chọn quỹ. Nguồn thứ hai (chọn thành viên trùng tên) làm luật đó
     * sai, và cái sai rơi vào nhánh fallback gửi thẳng UUID như một câu chat.
     *
     * Client KHÔNG được suy loại choose từ chỗ khác nữa: vắng `choiceKey` ⇒ không phải chip chọn.
     */
    choiceKey: z.string().min(1).optional(),
  })
  .strict();
export type CtaAction = z.infer<typeof ctaActionSchema>;

/** Phần phản hồi khi preview có `message` (đường reactive: chat/propose). */
export const aiPreviewReactiveSchema = z
  .object({
    intent: z.string(),
    reply: z.string(),
    /** Thẻ đề xuất (nháp) nếu có — null khi bị chặn / không phải write-intent. */
    proposalId: z.string().nullable(),
    /** @SEC-FAIL-02 — true khi LLM đang MÔ PHỎNG ⇒ CẤM ghi tiền. */
    simulated: z.boolean(),
    /** Bút toán sẽ/đã ghi. null khi không có đường tiền. */
    ledger: aiPreviewLedgerPreviewSchema.nullable(),
    /**
     * 🔴 @AI-TRU-01 — bằng chứng đi KÈM đề xuất tiền. BẤT BIẾN: `ledger != null` ⇒ `evidence != null`
     * (có đường tiền mà không có bằng chứng = đúng bug G10-trust). null khi không có đường tiền.
     */
    evidence: aiPreviewEvidenceSchema.nullable().default(null),
    /** Lỗi (nếu nhánh lỗi) — mã ỔN ĐỊNH. null khi chạy trơn. */
    error: aiErrorPayloadSchema.nullable(),
    /**
     * 🔴 US-AI-0271-S4 — CTA của turn (chip năng lực / deep_link GUI).
     *
     * BUG NÓ VÁ (PO 17/07: "không thấy option nào"): DTO này `.strict()` và TRƯỚC ĐÂY KHÔNG có
     * trường `ctas` ⇒ chips do `routeTurn` dựng (`capabilityChips()` — từ manifest năng lực THẬT)
     * bị CẮT trước khi tới UI admin preview. Mobile (Flutter `AiCtaRow`) render chips bình thường
     * ⇒ preview và app thật KHÁC NHAU ở đúng chỗ preview sinh ra để phản ánh trung thực.
     *
     * Lưu ý contract: `clarify` KHÔNG BAO GIỜ mang `options` — `options[]` thuộc turn type `choices`
     * (xem `choiceSpecSchema`). Thứ user thấy dưới bubble clarify là CHIPS, chính là trường này.
     *
     * `.optional().default([])` — tương thích NGƯỢC hai chiều: payload cũ (không có ctas) vẫn parse
     * được, VÀ các nhánh preview khác (lỗi/ledger/proposal — không phải turn router) dựng object
     * không cần khai `ctas: []` thừa. Sau parse luôn là mảng (không bao giờ undefined).
     */
    ctas: z.array(ctaActionSchema).optional().default([]),
  })
  .strict();
export type AiPreviewReactive = z.infer<typeof aiPreviewReactiveSchema>;

/**
 * base fields của aiPreviewResponse (mọi field TRỪ `formTurn`). Tách ra để có thể `.extend(formTurn)`
 * SAU khi `aiTurnSchema` được khai báo cuối file (tránh temporal-dead-zone: aiTurnSchema định nghĩa ở
 * dưới). aiPreviewResponseSchema THẬT (kèm formTurn) khai báo sau aiTurnSchema.
 */
const aiPreviewResponseBaseSchema = z.object({
  sessionId: z.string().uuid(),
  mode: aiPreviewModeSchema,
  simulate: aiPreviewSimulateSchema,
  /** 🔴 CÙNG schema với app thật (GET /v1/ai/context.proactive) — fidelity contract. */
  proactive: z.array(aiProactiveFlowSchema),
  /** 🔴 US-AI-0295-S03 — lượt chào GỘP (cùng nguồn với proactive, khác cách TRÌNH BÀY). */
  greetingTurn: aiGreetingTurnSchema.nullable().default(null),
  explain: aiOrchestrationExplainSchema,
  reactive: aiPreviewReactiveSchema.nullable(),
  /** @AI-ERR-04 — banner demo (chỉ khi simulated). null khi LLM thật. */
  demoBanner: z.string().nullable(),
  /**
   * 🔴 US-E23.F5-0335-S05 (@UC9524) — BẰNG CHỨNG SCRUB PII: câu admin gõ, TRƯỚC và SAU khi scrub.
   *
   * VÌ SAO ĐÁNG PHƠI: *"PII công dân VN không rời hệ thống — scrub TRƯỚC khi gửi LLM"* là một bất
   * biến **vô hình**. Nó chạy trong `base-llm-client`, không ai thấy, và cách duy nhất để biết nó
   * còn sống là đọc code hoặc tin lời hứa. Đưa cặp raw↔scrubbed ra màn preview biến nó thành thứ
   * **kiểm chứng được bằng mắt**: admin gõ một số điện thoại thật, thấy ngay nó biến thành gì trước
   * khi rời hệ thống. Bất biến có người nhìn là bất biến khó chết lặng.
   *
   * AN TOÀN: `raw` ở đây là câu **chính admin vừa gõ vào ô thử nghiệm** — không phải PII moi từ DB
   * của người dùng thật (preview chạy trên sandbox). Route đã `@Roles("SUPER_ADMIN","OPS")`.
   * `null` khi lượt đó không có `message` (đường proactive).
   */
  piiScrub: z
    .object({
      raw: z.string(),
      scrubbed: z.string(),
      /** true ⇒ scrubber ĐÃ đổi gì đó. Cho phép khoá bằng test thay vì so chuỗi bằng mắt. */
      changed: z.boolean(),
    })
    .strict()
    .nullable()
    .default(null),
});

/**
 * GET /v1/admin/ai/preview/actors (US-AI.F5-0234 · @UC9400) — DANH SÁCH ĐÓNG VAI **THẬT** từ sandbox.
 *
 * Admin UI KHÔNG được hardcode user/quỹ (dữ liệu giả = màn nói dối). Dropdown "Đóng vai" + "Quỹ đích"
 * đọc từ đây. Sandbox chưa dựng ⇒ mảng rỗng (UI nói "chạy Reset để dựng nhóm Demo"), KHÔNG bịa.
 */
export const aiPreviewActorSchema = z
  .object({
    userId: z.string().uuid(),
    memberId: z.string(),
    displayName: z.string().nullable(),
    role: z.string(),
    groupId: z.string(),
    groupName: z.string(),
  })
  .strict();
export type AiPreviewActor = z.infer<typeof aiPreviewActorSchema>;

export const aiPreviewFundSchema = z
  .object({
    fundId: z.string().uuid(),
    name: z.string(),
    groupId: z.string(),
  })
  .strict();
export type AiPreviewFund = z.infer<typeof aiPreviewFundSchema>;

export const aiPreviewActorsResponseSchema = z
  .object({
    actors: z.array(aiPreviewActorSchema),
    funds: z.array(aiPreviewFundSchema),
  })
  .strict();
export type AiPreviewActorsResponse = z.infer<typeof aiPreviewActorsResponseSchema>;

/** POST /v1/admin/ai/preview/reset — dựng lại sandbox về snapshot gốc. */
export const aiPreviewResetResponseSchema = z
  .object({
    reset: z.literal(true),
    groups: z.number().int(),
    funds: z.number().int(),
    members: z.number().int(),
  })
  .strict();
export type AiPreviewResetResponse = z.infer<typeof aiPreviewResetResponseSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// US-AIF.P0-0236-S01 (@UC9410) — AiTurn union: contract luồng AI FORM-DRIVEN đa lượt.
//
// ĐỨNG CẠNH schema AI cũ (aiChatResponse/aiQueryResponse/aiProposal) — KHÔNG sửa/xoá chúng.
// Server trả `AiTurn` sau mỗi lượt chat qua /v1/ai/form/* (T2/T3 dựng router/engine sau).
//
// BẤT BIẾN:
//  - reply KHÔNG rỗng/whitespace (câu trả lời thỏa đáng); ctas.length >= 1 (KHÔNG ngõ cụt).
//  - Money = string minor VND (no-float, B1) ở mọi field tiền (FormField.value type=money, summary).
//  - .strict() chặn field lạ/over-post ở mọi object (mirror bất biến ai/index.ts:8-9).
//  - superRefine CTA per-type (unsupported/redirect→deep_link; error→retry|deep_link theo retryable;
//    confirm→confirm+reject; form→spec; choices→choices; answer/clarify/capabilities→chip).
//  - FormField.options CHỈ hợp lệ ở single_choice/multi_choice/member_picker/fund_picker.
//  - Tái dùng aiPreviewEvidenceSchema (đường tiền, @AI-TRU-01) + aiErrorPayloadSchema (type=error).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Money minor VND string (no-float, B1): số nguyên KHÔNG âm (0 hợp lệ cho hiển thị "0đ" nếu cần).
 * Reject float ("500000.50"), số âm, chuỗi rỗng, ký tự lạ.
 */
const MONEY_MINOR_REGEX = /^(0|[1-9]\d{0,18})$/;

/** Reply không rỗng/whitespace (câu trả lời thỏa đáng — AC8). */
const aiTurnReplySchema = z.string().trim().min(1);

/** Discriminator — 10 loại turn (9 của router + `proposal` thêm ở US-AI-0423 phần 2). */
export const aiTurnTypeSchema = z.enum([
  "answer",
  "clarify",
  "choices",
  "form",
  "confirm",
  "unsupported",
  "capabilities",
  "error",
  "redirect",
  /**
   * 🔴 US-AI-0423 phần 2 (@UC0072) — THẺ ĐỀ XUẤT đã điền sẵn, sửa được, chờ người duyệt.
   *
   * Vì sao là một LOẠI RIÊNG chứ không tái dùng `confirm`: hai lượt này chốt qua **hai cửa khác
   * nhau** — `confirm` ⇒ `POST /ai/form/{sessionId}/confirm`, còn lượt này ⇒
   * `POST /ai/proposals/{proposalId}/confirm`. Gộp chung một loại nghĩa là client phải suy ra cửa
   * nào từ một field tuỳ chọn, và **đoán sai = bấm nhầm cửa trên đường ghi tiền thật**.
   *
   * Tách loại làm trạng thái sai KHÔNG BIỂU DIỄN ĐƯỢC ở cả hai đầu: zod phân biệt bằng
   * `discriminatedUnion`, Dart phân biệt bằng sealed class + switch vét cạn. Không bên nào cần nhớ luật.
   *
   * Chỉ lượt này mới có `repropose` (sửa số/tên ngay trong thẻ) — `confirm` của form không có, và đó
   * chính là lý do đường proposal phải sống chứ không bị gộp (`ai.controller.ts:159-163`: ghép lại câu
   * chat để sửa sẽ khớp động từ MUTATE ⇒ luôn bị từ chối ⇒ vòng lặp chết).
   */
  "proposal",
]);
export type AiTurnType = z.infer<typeof aiTurnTypeSchema>;

/** Kiểu field trong FormSpec. money = string minor (no-float). list = field nhiều dòng (repeatable). */
export const fieldTypeSchema = z.enum([
  "text",
  "money",
  "date",
  "single_choice",
  "multi_choice",
  "toggle",
  "member_picker",
  "fund_picker",
  "list",
]);
export type FieldType = z.infer<typeof fieldTypeSchema>;

/** Một lựa chọn (value/label + sublabel gợi ý) — SERVER điền, KHÔNG do LLM bịa. */
const optionSchema = z
  .object({
    value: z.string(),
    label: z.string(),
    sublabel: z.string().optional(),
  })
  .strict();

/**
 * ItemColumn — mô tả MỘT cột con của field `list` (per-member `{memberId, amountMinor}`, tier
 * `{label, amountMinor}`…). `type` là FieldType của cột con — KHÔNG được `"list"` (no nested list,
 * siết ở superRefine của formFieldSchema). options SERVER điền (member_picker/choice cột con).
 */
export const itemColumnSchema = z
  .object({
    key: z.string().min(1),
    label: z.string(),
    type: fieldTypeSchema,
    required: z.boolean(),
    options: z.array(optionSchema).optional(),
  })
  .strict();
export type ItemColumn = z.infer<typeof itemColumnSchema>;

/**
 * ListRow — một dòng của field `list`: map key cột con → value string (null = chưa điền). Value luôn
 * là string (money = minor string no-float, siết ở superRefine của formFieldSchema theo itemSchema).
 */
export const listRowSchema = z.record(z.string(), z.string().nullable());
export type ListRow = z.infer<typeof listRowSchema>;

/**
 * FormField — một ô trong FormSpec. money = string minor (no-float). options SERVER điền, CHỈ hợp lệ ở
 * single_choice/multi_choice/member_picker/fund_picker (refine — AC5). value nullable (chưa điền).
 *
 * Field `list` (repeatable) KHÔNG dùng `value` — mô tả cột con qua `itemSchema` + mang dòng qua `rows`.
 * Union giữ zero-regression: nhánh single-value (`value: z.string().nullable()`) Y NGUYÊN; `itemSchema`/
 * `rows` CHỈ hợp lệ ở type="list", non-list KHÔNG được mang chúng (siết superRefine).
 */
export const formFieldSchema = z
  .object({
    key: z.string().min(1),
    label: z.string(),
    type: fieldTypeSchema,
    required: z.boolean(),
    options: z.array(optionSchema).optional(),
    value: z.string().nullable().optional(),
    help: z.string().optional(),
    /** CHỈ type="list": mô tả cột con (≥1). */
    itemSchema: z.array(itemColumnSchema).optional(),
    /** CHỈ type="list": các dòng đã điền. */
    rows: z.array(listRowSchema).optional(),
  })
  .strict()
  .superRefine((field, ctx) => {
    // AC5 — options chỉ ở choice/picker type.
    const optionBearing: FieldType[] = [
      "single_choice",
      "multi_choice",
      "member_picker",
      "fund_picker",
    ];
    if (field.options !== undefined && !optionBearing.includes(field.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["options"],
        message: `options chỉ hợp lệ ở single_choice/multi_choice/member_picker/fund_picker (nhận type=${field.type})`,
      });
    }
    // AC4 — money field value phải là string minor (no-float).
    if (field.type === "money" && field.value != null) {
      if (!MONEY_MINOR_REGEX.test(field.value)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["value"],
          message: "Field money: value phải là string minor VND nguyên không âm (no-float)",
        });
      }
    }

    if (field.type === "list") {
      // list ⇒ itemSchema bắt buộc (≥1 cột con), value vắng/null.
      if (field.itemSchema === undefined || field.itemSchema.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["itemSchema"],
          message: "Field list: itemSchema bắt buộc, ≥1 cột con.",
        });
        return;
      }
      if (field.value != null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["value"],
          message: "Field list KHÔNG dùng value — mang dòng qua rows.",
        });
      }
      const columnKeys = new Set<string>();
      for (const col of field.itemSchema) {
        // no nested list.
        if (col.type === "list") {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["itemSchema"],
            message: `Cột con "${col.key}" không được type="list" (không lồng list).`,
          });
        }
        columnKeys.add(col.key);
      }
      // rows (nếu có): mỗi dòng có ĐÚNG key itemSchema; money cột con no-float.
      if (field.rows !== undefined) {
        field.rows.forEach((row, rowIdx) => {
          const rowKeys = Object.keys(row);
          for (const col of field.itemSchema!) {
            if (!(col.key in row)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["rows", rowIdx, col.key],
                message: `Dòng ${rowIdx}: thiếu cột con "${col.key}".`,
              });
            }
          }
          for (const rk of rowKeys) {
            if (!columnKeys.has(rk)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["rows", rowIdx, rk],
                message: `Dòng ${rowIdx}: key "${rk}" không thuộc itemSchema.`,
              });
            }
          }
          for (const col of field.itemSchema!) {
            if (col.type === "money") {
              const cellValue = row[col.key];
              if (cellValue != null && !MONEY_MINOR_REGEX.test(cellValue)) {
                ctx.addIssue({
                  code: z.ZodIssueCode.custom,
                  path: ["rows", rowIdx, col.key],
                  message: `Dòng ${rowIdx} cột "${col.key}": money phải là string minor no-float.`,
                });
              }
            }
          }
        });
      }
    } else {
      // non-list ⇒ KHÔNG mang itemSchema/rows (giữ single-value contract y nguyên).
      if (field.itemSchema !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["itemSchema"],
          message: `itemSchema chỉ hợp lệ ở type="list" (nhận type=${field.type}).`,
        });
      }
      if (field.rows !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rows"],
          message: `rows chỉ hợp lệ ở type="list" (nhận type=${field.type}).`,
        });
      }
    }
  });
export type FormField = z.infer<typeof formFieldSchema>;

/** FormSpec — bộ field + nhãn submit (type=form). */
export const formSpecSchema = z
  .object({
    fields: z.array(formFieldSchema),
    submitLabel: z.string().min(1),
  })
  .strict();
export type FormSpec = z.infer<typeof formSpecSchema>;

/** ChoiceSpec — chọn 1/nhiều từ danh sách (type=choices). chip choose chính là CTA. */
export const choiceSpecSchema = z
  .object({
    mode: z.enum(["single", "multi"]),
    key: z.string().min(1),
    options: z.array(optionSchema),
  })
  .strict();
export type ChoiceSpec = z.infer<typeof choiceSpecSchema>;

/**
 * FormSummary — bảng xác nhận (type=confirm). heavyConfirm=true ⇒ dialog cảnh báo NẶNG (@SEC-ONB-01).
 * `v` đã format sẵn cho người đọc ("500.000đ") — KHÔNG mang số tiền thô ở đây.
 */
export const formSummarySchema = z
  .object({
    lines: z.array(z.object({ k: z.string(), v: z.string() }).strict()),
    heavyConfirm: z.boolean(),
  })
  .strict();
export type FormSummary = z.infer<typeof formSummarySchema>;

/** Chip gợi ý hành động tiếp (tier read/write). */
export const chipSchema = z
  .object({
    label: z.string(),
    prompt: z.string(),
    tier: z.enum(["read", "write"]),
  })
  .strict();
export type Chip = z.infer<typeof chipSchema>;

// 🔴 US-E24.F14-0363-S04 (@UC9615) — ĐÃ GỠ cụm tiến-trình-nhiều-bước ("Step 1 of 4" + nút "Save")
// theo QĐ PO 06/08/2026: hằng trần số bước, schema tiến trình, type suy ra từ nó, và field tương ứng
// trong `aiTurnBaseFields` bên dưới. Schema refine rất chặt nhưng có **0 producer** — quét
// `apps/api/src` ra đúng 1 kết quả và nó là một COMMENT. Giữ lại = đặc-tả-giả cho người đọc sau.
// ⚠️ ĐỪNG khôi phục: `.strict()` của `aiTurnSchema` nay làm trạng thái này KHÔNG BIỂU DIỄN ĐƯỢC
// (payload cũ còn field đó ⇒ 400 ở biên). Guard `ai-wizard-removed.test.ts` quét 10 ký hiệu ⇒ 0.
// Resume phiên form là chuyện KHÁC và VẪN SỐNG (@UC9612/@UC9613).

/** Câu xen giữa form (user hỏi ngang giữa lúc điền) — server nhớ intent + field kế. */
const resumingFormSchema = z
  .object({
    intentCode: z.string(),
    nextField: z.string(),
  })
  .strict();

/**
 * Field NỀN chung mọi AiTurn. Discriminated union theo `type`. field option (choices/spec/summary/
 * evidence/error/resumingForm) khai báo ở đây để union giữ shape phẳng; refine per-type siết ràng buộc.
 */
const aiTurnBaseFields = {
  sessionId: z.string().min(1),
  reply: aiTurnReplySchema,
  ctas: z.array(ctaActionSchema),
  choices: choiceSpecSchema.optional(),
  spec: formSpecSchema.optional(),
  summary: formSummarySchema.optional(),
  evidence: aiPreviewEvidenceSchema.optional(),
  error: aiErrorPayloadSchema.optional(),
  resumingForm: resumingFormSchema.optional(),
  /**
   * 🔴 US-AI-0423 phần 2 (@UC0072) — CHỈ có ở `type:"proposal"` (refine ép hai chiều bên dưới).
   * Đây là khoá mà `POST /ai/proposals/{id}/confirm|repropose|cancel` dùng — KHÁC `sessionId`.
   */
  proposalId: z.string().uuid().optional(),
  // 🔴 US-E24.F14-0363-S04 (@UC9615) — field `wizard` ĐÃ GỠ (QĐ PO 06/08/2026). Nó `.optional()`
  // suốt vòng đời và chưa bao giờ được điền ⇒ gỡ là zero-regression cho 18 usecase lẻ.
};

const makeTurnVariant = (type: AiTurnType) =>
  z.object({ type: z.literal(type), ...aiTurnBaseFields }).strict();

const hasCtaKind = (ctas: readonly CtaAction[], kind: CtaKind): boolean =>
  ctas.some((c) => c.kind === kind);

/**
 * 🔴 superRefine CTA per-type — bất biến "KHÔNG ngõ cụt" (AC6/AC7). Áp SAU discriminatedUnion nên
 * biết chắc `t.type` + `t.ctas` đã đúng shape.
 */
const refineAiTurnCtas = (
  t: z.infer<ReturnType<typeof makeTurnVariant>>,
  ctx: z.RefinementCtx,
): void => {
  // 🔴 US-AI-0423 phần 2 — CHIỀU NGƯỢC LẠI, và đây mới là vế giữ an toàn: loại KHÁC `proposal`
  // TUYỆT ĐỐI không được mang `proposalId`. Thiếu vế này thì một turn `confirm` vẫn đính kèm được
  // `proposalId`, và client sẽ có hai khoá hợp lệ cùng lúc — đúng cái mơ hồ mà việc tách loại sinh ra
  // để xoá. Ràng buộc hai chiều ⇒ mỗi lượt có ĐÚNG MỘT cửa chốt.
  if (t.type !== "proposal" && t.proposalId != null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["proposalId"],
      message: `type=${t.type} KHÔNG được mang proposalId (chỉ type=proposal có cửa /ai/proposals/*).`,
    });
  }

  // AC6 — mọi turn ≥1 CTA (không ngõ cụt).
  if (t.ctas.length < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["ctas"],
      message: "Mọi AiTurn phải có ≥1 CTA (không ngõ cụt).",
    });
    return;
  }

  const needDeepLink = () => {
    if (!hasCtaKind(t.ctas, "deep_link")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ctas"],
        message: `type=${t.type} phải có ≥1 CTA kind=deep_link (ngõ cụt = cửa vào GUI, DP-7).`,
      });
    }
  };
  const needChip = () => {
    if (!hasCtaKind(t.ctas, "chip")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["ctas"],
        message: `type=${t.type} phải có ≥1 CTA kind=chip (gợi ý hành động tiếp).`,
      });
    }
  };

  switch (t.type) {
    case "unsupported":
    case "redirect":
      needDeepLink();
      break;
    case "error": {
      // error.retryable ⇒ retry; nếu không ⇒ deep_link.
      const retryable = t.error?.retryable === true;
      const wantKind: CtaKind = retryable ? "retry" : "deep_link";
      if (!hasCtaKind(t.ctas, wantKind)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ctas"],
          message: retryable
            ? "type=error retryable phải có CTA kind=retry."
            : "type=error không retryable phải có CTA kind=deep_link.",
        });
      }
      break;
    }
    case "confirm":
      if (!hasCtaKind(t.ctas, "confirm")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ctas"],
          message: "type=confirm phải có CTA kind=confirm.",
        });
      }
      if (!hasCtaKind(t.ctas, "reject")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ctas"],
          message: "type=confirm phải có CTA kind=reject (Huỷ).",
        });
      }
      if (t.summary == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["summary"],
          message: "type=confirm phải có summary.",
        });
      }
      break;
    /**
     * 🔴 US-AI-0423 phần 2 (@UC0072) — THẺ ĐỀ XUẤT. Ràng buộc CÙNG HỌ với `confirm` (phải có
     * summary + nút chốt + nút huỷ) nhưng chốt qua **cửa khác**, nên bắt buộc có `proposalId`.
     */
    case "proposal":
      if (!hasCtaKind(t.ctas, "confirm")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ctas"],
          message: "type=proposal phải có CTA kind=confirm.",
        });
      }
      if (!hasCtaKind(t.ctas, "reject")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["ctas"],
          message: "type=proposal phải có CTA kind=reject (Huỷ).",
        });
      }
      if (t.summary == null) {
        // Người dùng KÝ TÊN vào bảng này, không phải vào câu văn xuôi. Thiếu bảng ⇒ họ đang duyệt
        // một con số không nhìn thấy.
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["summary"],
          message: "type=proposal phải có summary (bảng người dùng ký tên vào).",
        });
      }
      if (t.proposalId == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["proposalId"],
          message: "type=proposal phải có proposalId (khoá của /ai/proposals/{id}/confirm).",
        });
      }
      break;
    case "form":
      if (t.spec == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["spec"],
          message: "type=form phải có spec (FormSpec).",
        });
      }
      break;
    case "choices":
      if (t.choices == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["choices"],
          message: "type=choices phải có choices (ChoiceSpec).",
        });
      }
      break;
    case "answer":
    case "clarify":
    case "capabilities":
      needChip();
      break;
  }
};

/**
 * 🔴 AiTurn — discriminated union theo `type` (10 loại) + superRefine CTA per-type. Một nguồn sự thật
 * api ↔ preview ↔ mobile cho luồng form-driven (@UC9410). .strict() ở từng variant chặn field lạ.
 */
export const aiTurnSchema = z
  .discriminatedUnion("type", [
    makeTurnVariant("answer"),
    makeTurnVariant("clarify"),
    makeTurnVariant("choices"),
    makeTurnVariant("form"),
    makeTurnVariant("confirm"),
    makeTurnVariant("unsupported"),
    makeTurnVariant("capabilities"),
    makeTurnVariant("error"),
    makeTurnVariant("redirect"),
    makeTurnVariant("proposal"),
  ])
  .superRefine(refineAiTurnCtas);
export type AiTurn = z.infer<typeof aiTurnSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// US-AIF.P0-0246 (@UC9418 · @gate money,ai) — PREVIEW FORM SURFACE (sandbox-only).
//
// Khai báo SAU `aiTurnSchema` (tránh temporal-dead-zone). `aiPreviewResponseSchema` = base ⊕ `formTurn`.
// 3 endpoint form đa-lượt trả `{ turn: aiTurnSchema }`. Mọi thứ chạy TRÊN DB SANDBOX (fail-closed 503).
// ═══════════════════════════════════════════════════════════════════════════

/**
 * POST /v1/admin/ai/preview response (base ⊕ formTurn).
 *
 * 🔴 `formTurn`: LƯỢT FORM ĐẦY ĐỦ khi câu chat kích hoạt một usecase form-driven (vd COL-EQUAL "tôi muốn
 * thu tiền"). `reactive` (shape LEGACY intent/reply) VẪN được điền để không vỡ assertion cũ, NHƯNG khối
 * này mang TRỌN `AiTurn` type=form (FormSpec + fund_picker options THẬT của sandbox) để admin preview
 * render đúng cái mobile sẽ render. null/absent khi câu không mở form (record-paid, query, clarify...) —
 * client cũ không vỡ (.optional()).
 */
export const aiPreviewResponseSchema = aiPreviewResponseBaseSchema
  .extend({ formTurn: aiTurnSchema.nullable().optional() })
  .strict();
export type AiPreviewResponse = z.infer<typeof aiPreviewResponseSchema>;

// ─── POST /v1/admin/ai/preview/form/{start,answer,confirm} ─────────────────────────────────────────
//
// 🔴 Bề mặt HTTP đa-lượt cho luồng form-driven TRÊN SANDBOX (fail-closed 503 nếu chưa cấu hình DB
// sandbox). Mỗi endpoint trả `{ turn: aiTurnSchema }` — AiTurn kế client render (form field kế / confirm
// summary / done). `mode`: chỉ `live` mới cho `confirm` tạo collection THẬT trong sandbox; `dry_run` mô
// tả nhưng KHÔNG tạo (mirror seam dry_run/live ở preview-runner). .strict() chặn over-post.

/** Body form/start — mở session form cho `intentCode`, đóng vai `actingAsUserId` trong sandbox. */
export const aiPreviewFormStartRequestSchema = z
  .object({
    actingAsUserId: z.string().uuid(),
    /** intentCode form (vd "COL-EQUAL"). Không có FormSpec ⇒ runner 400. */
    intentCode: z.string().trim().min(1).max(64),
    /** Quỹ đích tuỳ chọn (prefill fund_picker). Vắng ⇒ user chọn ở field fund_picker. */
    targetFundId: z.string().uuid().optional(),
    locale: z.enum(["vi", "en"]).default("vi"),
    mode: aiPreviewModeSchema.default("dry_run"),
  })
  .strict();
export type AiPreviewFormStartRequest = z.infer<typeof aiPreviewFormStartRequestSchema>;

/** Body form/answer — điền MỘT field của session. actor khoá lại ở runner (KHÔNG tin ngầm). */
export const aiPreviewFormAnswerRequestSchema = z
  .object({
    sessionId: z.string().uuid(),
    fieldKey: z.string().trim().min(1).max(64),
    /** Giá trị field (string; money = minor no-float — validate ở domain). */
    value: z.string().max(AI_MESSAGE_MAX_LENGTH),
    actingAsUserId: z.string().uuid(),
    mode: aiPreviewModeSchema.default("dry_run"),
  })
  .strict();
export type AiPreviewFormAnswerRequest = z.infer<typeof aiPreviewFormAnswerRequestSchema>;

/** Body form/confirm — chốt session. `live` ⇒ tạo collection sandbox; `dry_run` ⇒ mô tả, KHÔNG tạo. */
export const aiPreviewFormConfirmRequestSchema = z
  .object({
    sessionId: z.string().uuid(),
    actingAsUserId: z.string().uuid(),
    mode: aiPreviewModeSchema.default("dry_run"),
    // 🔴 B4 (US-AI-0286) — ca giả lập PHẢI đi kèm confirm form: `demo_mode` + `live` ⇒ CẤM GHI (fail-closed
    //   ở seam ghi, giống record-paid). Trước đây form confirm KHÔNG mang simulate ⇒ confirm ghi THẬT dưới
    //   banner "không ghi đồng nào" (banner nói dối — đúng DP-5). Mặc định "none" (tương thích ngược).
    simulate: aiPreviewSimulateSchema.default("none"),
  })
  .strict();
export type AiPreviewFormConfirmRequest = z.infer<typeof aiPreviewFormConfirmRequestSchema>;

/**
 * 🔴 US-AI-0296-S02 — Body form/cancel. ĐÓNG phiên form THẬT (0 bút toán, idempotent).
 *
 * VÌ SAO PHẢI CÓ: preview trước đây KHÔNG có endpoint huỷ nào, nên nút "Huỷ" của form chỉ là chip gửi
 * chữ "huỷ" như câu chat ⇒ session VẪN SỐNG ⇒ form render lại (bug PO 21/07). Không có endpoint thì
 * nút không thể huỷ thật được, dù client sửa thế nào.
 */
export const aiPreviewFormCancelRequestSchema = z
  .object({
    sessionId: z.string().uuid(),
    actingAsUserId: z.string().uuid(),
  })
  .strict();
export type AiPreviewFormCancelRequest = z.infer<typeof aiPreviewFormCancelRequestSchema>;

/* ===========================================================================
 * US-E23.F5-0335-S02 (@UC9525) — VÒNG ĐỜI THẺ ĐỀ XUẤT trong preview.
 *
 * 🔴 VÙNG TRẮNG ĐANG VÁ: preview trước đây trả `proposalId: null` ở MỌI nhánh — nó mô phỏng bút toán
 * nhưng KHÔNG hề đẻ thẻ, nên admin không có cách nào xem hai thao tác *"Sửa đề xuất"* và *"Huỷ"* chạy
 * thế nào. Đó không phải "thiếu độ phủ đường hỏng", mà là cả một vòng đời chưa từng diễn ở preview.
 *
 * 🔴 AN TOÀN — thẻ preview nằm TRỌN trong sandbox: `AiProposalService` nay nhận seam `db`
 * (mirror `AiScopeService.resolveFund(…, db)` và `CreateExpenseInput.db` — `db-seam.ts` là nguồn lý
 * lẽ, KHÔNG cơ chế mới), preview truyền `PreviewSandboxClient` vốn TỪ CHỐI BOOT nếu URL trùng
 * `DATABASE_URL`.
 *
 * 🔴 PHẠM VI CỐ Ý HẸP: chỉ `repropose` + `cancel` — hai thao tác **0 BÚT TOÁN**. `confirm` (đường ghi
 * tiền) KHÔNG mở ở preview: human-in-the-loop (AI-INV-4) nghĩa là thẻ thật phải do người bấm trong
 * app thật, và mở đường confirm ở màn quản trị là đúng thứ bất biến đó cấm.
 * ========================================================================= */

/** Body `POST /v1/admin/ai/preview/proposal/cancel` — đóng thẻ nháp trong sandbox. 0 bút toán. */
export const aiPreviewProposalCancelRequestSchema = z
  .object({
    proposalId: z.string().uuid(),
    actingAsUserId: z.string().uuid(),
  })
  .strict();
export type AiPreviewProposalCancelRequest = z.infer<
  typeof aiPreviewProposalCancelRequestSchema
>;

/**
 * Body `POST /v1/admin/ai/preview/proposal/repropose` — SỬA CÓ CẤU TRÚC (không ghép câu chat).
 * Patch rỗng ⇒ 400 ở biên: "sửa mà không đổi gì" là request vô nghĩa, và ở app thật nó từng tạo
 * VÒNG LẶP CHẾT (ghép chuỗi "Sửa đề xuất…" ⇒ khớp động từ MUTATE ⇒ confirm luôn từ chối).
 */
export const aiPreviewProposalReproposeRequestSchema = z
  .object({
    proposalId: z.string().uuid(),
    actingAsUserId: z.string().uuid(),
    memberName: z.string().trim().min(1).optional(),
    /** Tiền là STRING minor (B1 no-float) — mirror đường thật. */
    amountMinor: z.string().regex(/^\d+$/).optional(),
  })
  .strict()
  .refine((b) => b.memberName !== undefined || b.amountMinor !== undefined, {
    message: "Cần ít nhất một thay đổi (tên thành viên hoặc số tiền).",
  });
export type AiPreviewProposalReproposeRequest = z.infer<
  typeof aiPreviewProposalReproposeRequestSchema
>;

/** Response chung cho hai thao tác trên — trạng thái thẻ SAU thao tác. */
export const aiPreviewProposalResponseSchema = z
  .object({
    proposalId: z.string().uuid(),
    /** Thẻ bị thay (chỉ có ở repropose) — cho admin thấy thẻ cũ đã ĐÓNG, không treo lơ lửng. */
    replacedProposalId: z.string().uuid().nullable(),
    resolved: z.boolean(),
    outcome: z.string().nullable(),
    reply: z.string(),
    memberName: z.string().nullable(),
    /** STRING minor (no-float). null khi thẻ không mang số tiền. */
    amountMinor: z.string().nullable(),
  })
  .strict();
export type AiPreviewProposalResponse = z.infer<typeof aiPreviewProposalResponseSchema>;

/**
 * Response 3 endpoint form: AiTurn kế client render (form field kế / confirm / done).
 *
 * 🔴 US-AI-0298-S03 — THÊM `trace`: trước đây shape này CHỈ có `{ turn }`, không kèm explain/trace nào.
 * Hệ quả đã đo: chỉ lượt ĐẦU (POST /preview) có trace; `answer`/`confirm`/`cancel` chạy hoàn toàn
 * **trong bóng tối** — đúng vùng mà bug duplicate-turn và "Huỷ không huỷ" xảy ra.
 * Trace ở đây là **TOÀN BỘ dòng thời gian tích luỹ của phiên** (S04 lo store), không phải riêng lượt này.
 */
export const aiPreviewFormTurnResponseSchema = z
  .object({ turn: aiTurnSchema, trace: z.array(traceStepSchema).default([]) })
  .strict();
export type AiPreviewFormTurnResponse = z.infer<typeof aiPreviewFormTurnResponseSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// 🔴 US-AIF.P5-0261 (@UC9450 · @gate money,ai) — BỀ MẶT FORM NGƯỜI DÙNG THẬT `/v1/ai/form/*`.
//
// LÝ DO TỒN TẠI: 18 usecase form (COL-*/EXP-*/DEBT-*/VOTE-*) trước đây CHỈ chạy được trong
// `POST /v1/admin/ai/preview` — bề mặt ADMIN-only nối DB SANDBOX. Người dùng THẬT không có đường nào
// chạm tới ⇒ toàn bộ 4 phase là code mồ côi. Khối này khai hợp đồng cho controller THẬT (DB THẬT).
//
// 🔴 KHÁC BIỆT CÓ CHỦ Ý so với preview (đọc kỹ trước khi "thống nhất" hai bên):
//  1. KHÔNG có `actingAsUserId` — actor RÚT TỪ TOKEN (getUserActor). Nhận actor từ body ở kênh end-user
//     = lỗ hổng đóng-vai (user A ghi tiền dưới danh nghĩa B). Preview có field đó vì admin ĐÓNG VAI là
//     tính năng CỦA preview, và bề mặt đó đã chốt RBAC SUPER_ADMIN/OPS.
//  2. KHÔNG có `mode` (dry_run/live) — người dùng thật KHÔNG chạy thử vào sổ. Mọi confirm là THẬT.
//     `dry_run` là công cụ preview cho admin xem trước; phơi ra cho user = thẻ "đã xong" mà 0 bút toán.
//  3. Response = `AiTurn` TRẦN (KHÔNG bọc `{turn}`) — khớp `AiTurn.fromJson(json)` mà mobile ĐÃ gọi
//     (`assistant_repository.dart:270,283,296,307,317`). Preview bọc `{turn}` vì admin UI đọc kiểu đó.
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Body POST /v1/ai/form/message — câu tự do; server `routeTurn` phân loại rồi mở session form nếu là
 * write-intent. `sessionId` có ⇒ tiếp phiên đang điền (câu xen ngang KHÔNG mất draft).
 *
 * `scope`: "all" | fundId (UUID) — MIRROR `aiChatRequestSchema.scope` (một quy ước scope cho cả kênh AI).
 * scope=fundId ⇒ quỹ ĐÃ chốt ⇒ router không hỏi lại "quỹ nào".
 */
export const aiFormMessageRequestSchema = z
  .object({
    message: z.string().trim().min(1).max(AI_MESSAGE_MAX_LENGTH),
    scope: z.union([z.literal("all"), z.string().uuid()]),
    /**
     * @deprecated Vế `z.preprocess` chấp nhận chuỗi `"new"` là **shim tương thích ngược** của
     * US-AI-0463-S01 (thẻ `T-32`, lỗ hổng `G-27` — `docs/ai-audit/08-ke-hoach-khac-phuc.md`).
     * Server hôm nay VẪN PHÁT `"new"` ở lượt đầu (`ai-form-router.service.ts` sentinel `?? "new"`)
     * rồi TỰ TỪ CHỐI chính giá trị đó bằng 400 khi client gửi lại ⇒ trợ lý im lặng từ lượt thứ hai
     * trên MỌI app đã cài. App phân phối qua store, không deploy cùng nhịp với BE.
     *
     * Map về `undefined` **ở BIÊN** để không dòng code nào phía sau nhìn thấy chuỗi đó — sau
     * `parse`, giá trị y hệt trường hợp client không gửi field.
     *
     * 🔴 **GỠ Ở BƯỚC 2** (`US-AI-0463-S04`/`S05`): bỏ sentinel, siết `aiTurnBaseFields.sessionId`
     * thành `uuid().optional()` để hai đầu cùng một luật. Chỉ chạy khi telemetry chứng minh client
     * cũ đã rút. Test ghim: `ai-form-message-request.test.ts`.
     */
    sessionId: z
      .preprocess((v) => (v === "new" ? undefined : v), z.string().uuid().optional())
      // 🔴 `.optional()` NGOÀI CÙNG là bắt buộc, không phải thừa: `z.preprocess` trả về
      //   `ZodEffects`, và ở tầng object thì `ZodEffects` được coi là **required** —
      //   bộ sinh OpenAPI đã bắt đúng điều đó (`assert-openapi-fresh` đòi thêm
      //   `sessionId` vào `required`). Runtime vẫn nhận vắng, nên nếu thiếu vế này thì
      //   hợp đồng CÔNG KHAI nói dối: quảng cáo một field bắt buộc mà server không đòi.
      .optional(),
    /**
     * 🔴 US-E24.F14-0363-S03 (@UC9614) — hội thoại NGUỒN: có ⇒ mọi lượt của phiên form này vào
     * `ai_messages` của hội thoại đó (Chat History, Figma 201:20452). VẮNG ⇒ form mở NGOÀI luồng chat
     * ⇒ KHÔNG ghi lịch sử (cột `AiFormSession.conversationId` nullable là CỐ Ý). Hội thoại của người
     * khác ⇒ 404 (IDOR, qua `AiConversationService.ensureConversation`).
     */
    conversationId: z.string().uuid().optional(),
  })
  .strict();
export type AiFormMessageRequest = z.infer<typeof aiFormMessageRequestSchema>;

/**
 * Body POST /v1/ai/form/:sessionId/answer — điền field. Mobile gửi MAP nhiều field
 * (`{answers: {key: value}}` — `assistant_repository.dart:274-284`) trong khi state machine điền MỘT
 * slot/lượt ⇒ server áp TUẦN TỰ theo thứ tự khai báo trong FormSpec (không phải thứ tự key của JSON —
 * thứ tự key JS không đáng tin cho ngữ nghĩa). Field lỗi ⇒ 400 và các field TRƯỚC đó đã ghi vẫn giữ
 * (fillSlot bất biến — draft không mất).
 *
 * Money = string minor VND (no-float, B1) — validate ở domain `validateFieldValue`.
 */
export const aiFormAnswerRequestSchema = z
  .object({
    answers: z.record(z.string().trim().min(1).max(64), z.string().max(AI_MESSAGE_MAX_LENGTH)),
  })
  .strict();
export type AiFormAnswerRequest = z.infer<typeof aiFormAnswerRequestSchema>;

/**
 * Body POST /v1/ai/form/:sessionId/choose — chọn 1 option (type=choices). Shape khớp mobile
 * (`{key, value}` — `assistant_repository.dart:287-297`). Về bản chất là answer 1 field ⇒ server nối
 * CÙNG `answerForm` (KHÔNG engine thứ hai): server resolve định danh THẬT, client KHÔNG bịa.
 */
export const aiFormChooseRequestSchema = z
  .object({
    key: z.string().trim().min(1).max(64),
    value: z.string().max(AI_MESSAGE_MAX_LENGTH),
  })
  .strict();
export type AiFormChooseRequest = z.infer<typeof aiFormChooseRequestSchema>;

// ─── 🔴 US-E24.F14-0363-S02 (@UC9613) — list phiên form ĐANG DỞ của actor ──────────────────────
//
// Bug #18 (re-audit 06/08/2026): `ai_form_sessions` giữ `formState` sống 30' và index
// `[actorUserId, status]` (schema.prisma:2686) đã dựng sẵn kèm comment "list session ĐANG điền của
// actor (IDOR-scoped)" — nhưng không có endpoint nào đọc ra.

/** Trần số phiên trả về một trang — cap read defensive (mirror MAX_SCHEDULES_PER_FUND). */
export const AI_FORM_SESSIONS_MAX_LIMIT = 100;

/**
 * Trạng thái phiên **quay lại được**. Enum ĐÓNG và CỐ Ý hẹp hơn `ai_form_sessions.status`:
 * `done`/`cancelled` không phải "phiên dở" — và `GET /v1/ai/form/{sessionId}` trả 409 cho chúng,
 * nên liệt kê chúng ở đây là mời người dùng bấm vào một ngõ cụt.
 */
export const aiFormResumableStatusSchema = z.enum(["collecting", "confirm"]);
export type AiFormResumableStatus = z.infer<typeof aiFormResumableStatusSchema>;

/**
 * Query GET /v1/ai/form/sessions — `.strict()` + cursor (KHÔNG offset, CLAUDE.md §7).
 * `status` mặc định `collecting` (câu hỏi thường gặp nhất: "tôi đang điền dở cái gì?").
 */
export const aiFormSessionsListQuerySchema = z
  .object({
    status: aiFormResumableStatusSchema.default("collecting"),
    cursor: z.string().max(AI_CURSOR_MAX_LENGTH).optional(),
    limit: z.coerce.number().int().min(1).max(AI_FORM_SESSIONS_MAX_LIMIT).optional(),
  })
  .strict();
export type AiFormSessionsListQuery = z.infer<typeof aiFormSessionsListQuerySchema>;

/**
 * 🔴 Item phiên trong list — METADATA, KHÔNG `formState`.
 *
 * `formState` chứa số tiền + tên người + quỹ đích của một bản nháp đang điền. List chỉ để CHỌN phiên
 * nào quay lại; nội dung thì gọi `GET /v1/ai/form/{sessionId}` (đã IDOR-scoped, @UC9612). Ít bề mặt
 * lộ hơn, và list nhẹ hơn. `.strict()` làm "lỡ tay thêm formState" KHÔNG BIỂU DIỄN ĐƯỢC; test
 * contracts còn hardcode danh sách khoá để một field mới không lặng lẽ trôi ra ngoài.
 *
 * Tiến độ trả dạng ĐẾM (`filled`/`required`) — đủ để người dùng biết còn xa hay gần xong mà không
 * phơi giá trị nào. Server tính từ `FORM_SPEC_CATALOG` (in-memory) ⇒ 0 query/phiên (chống N+1).
 */
export const aiFormSessionListItemSchema = z
  .object({
    sessionId: z.string().uuid(),
    intentCode: z.string().min(1),
    status: aiFormResumableStatusSchema,
    filledFieldCount: z.number().int().min(0),
    requiredFieldCount: z.number().int().min(0),
    createdAt: z.string(),
    updatedAt: z.string(),
    expiresAt: z.string(),
  })
  .strict();
export type AiFormSessionListItem = z.infer<typeof aiFormSessionListItemSchema>;

export const aiFormSessionsListResponseSchema = z
  .object({
    data: z.array(aiFormSessionListItemSchema),
    pageInfo: z
      .object({
        hasNext: z.boolean(),
        nextCursor: z.string().nullable(),
      })
      .strict(),
  })
  .strict();
export type AiFormSessionsListResponse = z.infer<typeof aiFormSessionsListResponseSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// US-AI-0424 (@UC9430 · @gate ai,pii) — ĐÍNH TỆP VÀO MỘT PHIÊN FORM AI.
//
// Nền cho "upload hoá đơn ⇒ AI tự điền form khoản chi" (QĐ người dùng 16/08/2026). Đợt này CHỈ đưa
// tệp lên và ràng nó vào đúng phiên; **OCR NGOÀI phạm vi** (provider là seam DEFER, và chọn nhà cung
// cấp là quyết định có chi phí + có PII công dân VN — CLAUDE.md §4).
//
// Ba tiền lệ được bám, không phát minh (`dispute-evidence.controller.ts:59-78`):
//  1. route GẮN VỚI PHIÊN ngay từ bước upload ⇒ RBAC kiểm TRƯỚC khi tốn băng thông, và không đẻ ra
//     một bãi chứa tệp không chủ;
//  2. multipart chứ không presigned-PUT ⇒ server giữ được điểm kiểm magic-byte;
//  3. handler KHÔNG `@Audited` ⇒ không bọc upload R2 trong một transaction 5 giây.
// ═══════════════════════════════════════════════════════════════════════════

/** Tên field multipart — dùng chung với `me/avatar`, `funds/:id/qr`, `disputes/:id/evidence/upload`. */
export const AI_FORM_ATTACHMENT_FILE_FIELD = "file" as const;

/** Vùng chứa riêng trên R2: lifecycle của tệp phiên AI ngắn hơn hẳn bằng chứng khiếu nại. */
export const AI_FORM_ATTACHMENT_KEY_PREFIX = "ai-form" as const;

/** Đuôi được nhận — ảnh hoá đơn. PDF **chưa** nhận: sniffer hiện chỉ biết ba loại ảnh này. */
const AI_FORM_ATTACHMENT_EXTENSIONS = ["jpg", "png", "webp"] as const;

export const AI_FORM_ATTACHMENT_MISSING_FILE_MESSAGE = "Thiếu tệp đính kèm (field 'file')." as const;
export const AI_FORM_ATTACHMENT_UPLOAD_FAILED_MESSAGE =
  "Tải tệp lên thất bại. Bạn thử lại nhé." as const;

/** Dựng key đúng dạng — dùng chung API + test, không đẻ bản chép tay thứ hai (§3.8). */
export function buildAiFormAttachmentKey(
  sessionId: string,
  objectUuid: string,
  ext: (typeof AI_FORM_ATTACHMENT_EXTENSIONS)[number] = "jpg",
): string {
  return `${AI_FORM_ATTACHMENT_KEY_PREFIX}/${sessionId}/${objectUuid}.${ext}`;
}

/**
 * `true` ⇔ `key` là key do SERVER sinh cho ĐÚNG `sessionId` này.
 *
 * 🔴 **Bài học "nộp bằng chứng ĐI MƯỢN" (PR #363, vá 09/08/2026)** — áp trước khi lỗ kịp xuất hiện:
 * ở đường khiếu nại, `attach` từng nhận `storageKey` là chuỗi bất kỳ, nên đọc key của case #1 rồi
 * đính sang case #2 là **nộp bằng chứng đi mượn**. Mọi đường tiêu thụ key của phiên AI về sau (bước
 * OCR, bước điền form) **phải gọi hàm này và fail-closed 422**.
 *
 * Hai chi tiết dễ làm hỏng lại, cả hai đều có test:
 * · **escape `sessionId`** trước khi nhét vào regex — quên thì `sessionId=".*"` thành wildcard và mở
 *   lại đúng lỗ vừa vá;
 * · UUID nhận nibble **`[1-8]`**, không `[1-5]` (bài học `uuidv7-guard-regex-404-trap`).
 */
export function isAiFormAttachmentKeyFor(sessionId: string, key: string): boolean {
  const uuid = "[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}";
  const escaped = sessionId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(
    `^${AI_FORM_ATTACHMENT_KEY_PREFIX}/${escaped}/${uuid}\\.(${AI_FORM_ATTACHMENT_EXTENSIONS.join("|")})$`,
    "i",
  );
  return re.test(key);
}

/**
 * Response `POST /v1/ai/form/{sessionId}/attachment`.
 *
 * `.strict()` + regex chặn URL: nếu tầng lưu trữ lỡ trả một URL đầy đủ thì **nổ ở đây**, thay vì để
 * URL đi tiếp và bị client đính ngược vào DB.
 */
export const aiFormAttachmentResponseSchema = z
  .object({ storageKey: z.string().min(1).max(512).regex(/^(?!https?:\/\/)/) })
  .strict();
export type AiFormAttachmentResponse = z.infer<typeof aiFormAttachmentResponseSchema>;
